// In-webapp Order Chat — first-class entry alongside the SMS bot. Drives the
// same engine (server/services/smsEngine.js) via /api/sms/chat/send. Open to
// providers, staff, and admins.

import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { ccmApi } from '../lib/api.js';
import '../styles/order-chat.css';
// GPT-Live Talk mode (PR 2): the framework-free socket/WebRTC client; this page
// only wires its events (turn frames → ingestReply, pin → pin frame, End → end).
import { LiveClient, socketUrlFor, stitchCaption, replyCoveredByTurn } from '../lib/live-client.js';
import { patientFragment, applyCanonicalName, pinDiverged, fragmentCoversPin, formatDob, patientSearchFragment, composerKeyAction } from '../lib/order-chat-patient-pin';
import { LabOrderCard, OrderWarnings } from '../components/order-doc/lab-order-card.jsx';
import { warningLines } from '../components/order-doc/order-doc-helpers.js';
import { DestinationEditor } from '../components/order-doc/destination-editor.jsx';
import { MedProductSlot, PickProductChip, RecipientSlot, showPickChip } from '../components/order-doc/med-product-slot.jsx';
import { cardSlot, chipRowIsOurs, markBatchResults, batchResultLines, isBatchReply, pinnedRowTerminal, reconcileBatchTurn, reconcileBatchTurns, rowIsLive, withResolution, withRosterState } from '../lib/order-batch.js';
import {
  OPEN_EACH_CARD_HINT, isBatchTurn, openedCount, confirmableSessionIds,
  markOpenedInBatch, carryOpenedForward, allOpened, openedSessionIdsFor,
  isExpanded, toggleExpanded, collapseFinished,
} from '../lib/order-batch-opened.js';
// Group-home refill sheets (PR-E): the composer's "Refill sheet…" item, its upload
// dialog, and the sheet itself in the transcript — whose needs-attention cards are drawn
// by this page's own Bubble (cardTurnFor), so every card control is the existing one.
import { ComposerPlusMenu } from '../components/refill-sheet/composer-plus-menu.jsx';
import { RefillSheetDialog } from '../components/refill-sheet/refill-sheet-dialog.jsx';
import { RefillSheetTurn } from '../components/refill-sheet/refill-sheet-turn.jsx';
import {
  createSheetPoller, foldSheetBatchTurns, newSheetTurn, withSheetJob, cardTurnFor,
  answerInPlace, lineOfSession, withInPlace, refusalText, inputCount, installInputCounter,
} from '../lib/refill-sheet.js';

// GPT-Live Talk mode (PR 2): how many stitched caption entries the rail keeps,
// how long a `reply` may wait for its delegation to END (the card lands any time
// before that: a staging tool can run for seconds after the sentence) before it
// becomes a bubble regardless, how much life a cached socket token needs to be
// reused on a reconnect, the plain sentence for each way a call can end without
// a `closed` the clinician asked for, and the status pill's words.
const LIVE_CAPTION_MAX = 8;
const LIVE_REPLY_WAIT_MS = 10000;
const LIVE_TOKEN_SLACK_S = 60;
const LIVE_CLOSE_TEXT = {
  sideband_lost: 'Live voice lost its connection to the voice service.',
  connection_lost: 'Live voice lost its connection.',
  client_lost: 'Live voice ended because this page went quiet.',
  remote_hangup: 'The voice service hung up.',
  expired: 'The live voice session expired.',
  content: 'The voice service ended the session.',
  stale: 'The live voice session was closed as stale.',
  backend_failed: 'The assistant could not reach its backend and ended the call.',
  auth_lost: 'Your session expired. Sign in again to keep talking.',
  create_failed: 'Live voice could not start.',
};
const LIVE_QUIET_CLOSE = new Set(['client_end', 'idle', 'max_duration', 'close_requested']);
function LIVE_PILL_TEXT(live) {
  if (live.state === 'connecting') return 'Connecting…';
  if (live.state === 'live') {
    if (live.phase === 'speaking') return 'Speaking';
    if (live.phase === 'thinking') return 'Thinking…';
    if (live.phase === 'reconnecting') return 'Reconnecting…';
    return 'Listening';
  }
  if (live.state === 'ended') return `Ended${Number.isFinite(live.seconds) ? ` · ${Math.round(live.seconds)} s` : ''}`;
  return '';
}

// Starter phrases that PREFILL the composer (cursor lands after "for ") so the
// provider just types the patient. Tapping one does not send — unlike a real order.
const STARTER_PHRASES = [
  'CBC and BMP for ',
  'Lipid panel and TSH for ',
  'metformin 500mg twice daily for ',
  'refill aspirin for ',
  'annual labs for ',
];

// R-CF-7: the fixed ['yes','no','undo','help'] row is GONE. Those words were
// the SMS adapter's protocol, and this client no longer speaks it — the card
// carries Confirm, Cancel and Undo, and the server refuses the words outright
// from a declaring request. What replaces it is `view.question.options`: the
// options the ROW itself holds, each carrying the id that answers it.

// Elation lab-order "Patient Condition" (content.fasting_method) options. Values
// must match the server FASTING_METHODS enum; labels mirror the Elation dialog.
const FASTING_OPTIONS = [
  { value: 'fasting_random', label: 'Random — fasting or non-fasting (default)' },
  { value: 'fasting_none', label: 'Non-Fasting' },
  { value: 'fasting_2_hours', label: 'Fasting — 2 hours' },
  { value: 'fasting_8_hours', label: 'Fasting — 8 hours' },
  { value: 'fasting_12_hour', label: 'Fasting — 12 hours' },
];

// Referral authorization_for default. Values are the server referral_type keys
// (medicalSpecialties REFERRAL_AUTH_TYPES); applied unless the message states a
// type ("for ongoing treatment", "procedure only").
const REFERRAL_AUTH_OPTIONS = [
  { value: 'consult', label: 'Consult — one visit only (default)' },
  { value: 'treatment', label: 'Treatment — ongoing care, includes consult' },
  { value: 'procedure', label: 'Procedure / testing only' },
];

function timeStr(ts) {
  const d = ts ? new Date(ts) : new Date();
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function orderIcon(type) {
  switch (type) {
    case 'lab':       return '🧪';
    case 'imaging':   return '🩻';
    case 'medication':return '💊';
    case 'referral':  return '👥';
    case 'cardiac':   return '🫀';
    case 'pulmonary': return '🫁';
    case 'sleep':     return '🌙';
    default:          return '📋';
  }
}

// `card` is the resolution's card DTO when the turn staged one, and for a
// MEDICATION it is the authority (L70). `applyOrderEdit` replaces the turn's
// `resolution` and nothing else — the `extractedOrders` it renders beside stay
// as the turn was staged — so a card that reads the ORDER showed the product,
// dose and quantity the pick had just replaced: "Valtrex Tab 500 mg · 500 mg ·
// #60 tablet" over a row the confirm would file as the 1 GM template, qty 4.
// The lab card has always rendered from its DTO (`LabOrderCard card={card}`);
// the medication summary card now does too. The `order` fallback is for a turn
// that carries no DTO at all — a completion turn, a flag-off practice.
//
// R-RX-14: a medication whose product is not chosen is titled by what the
// PROVIDER said — the catalog name appears only after a pick.
function orderTitle(o, card) {
  if (o.type === 'lab')        return `Lab order${o.labVendor ? ' (' + o.labVendor + ')' : ''}`;
  if (o.type === 'imaging')    return 'Imaging order';
  if (o.type === 'cardiac')    return 'Cardiac order';
  if (o.type === 'pulmonary')  return 'Pulmonary order';
  if (o.type === 'sleep')      return 'Sleep order';
  if (o.type === 'referral')   return `Referral${o.specialty ? ' to ' + o.specialty : ''}`;
  if (o.type === 'medication') {
    if (card && card.type === 'medication') {
      if (card.productStatus === 'unresolved') {
        return (card.dictated && card.dictated.drug) || o.drug_name || 'Medication';
      }
      return card.drug || o.resolvedName || o.drug_name || 'Medication';
    }
    return o.resolvedName || o.drug_name || 'Medication';
  }
  return o.type;
}

// The human destination text for a recipient-routed order's "Send now" confirm
// and completion turn. Prefers fax, falls back to email; null when neither is
// set (the card won't offer Send now then). Mirrors the DestinationEditor's
// fax-first channel default.
function destinationText(o) {
  if (!o) return null;
  if (o.recipient_fax) return o.recipient_fax;
  if (o.recipient_email) return o.recipient_email;
  return null;
}

// R-CF-1: "Send now" is offered only when a destination is KNOWN — the fax or
// email the provider gave, or one read off the matched contact's own record. A
// contact id was never proof of deliverability, and the server now refuses the
// same transmit before anything is signed; an enabled button the server would
// refuse is exactly what this removes.
//
// ONE reader: `card.sendReady` IS the server's `referralSendReady`, so the card
// never re-derives the rule. Reading `destination` alone was a second answer to
// the same question, and it disagreed — a fax dictated on a referral nobody was
// named for is a destination with NO recipient, and the button was offered for a
// send the gate always refused.
//
// A card carrying neither key is a row staged before this deploy (or a turn with
// no DTO): fall back to what the ORDER says, which is today's behaviour — the
// server is the authority either way.
function sendReadyFor(o, card) {
  if (card && typeof card.sendReady === 'boolean') return card.sendReady;
  const d = card && card.destination;
  if (d && typeof d.kind === 'string') return d.kind !== 'none';
  return !!destinationText(o);
}

// The "Send now" confirm names where the letter goes — never "in Elation".
// Returns { lead, value, tail } so the destination itself stays bold; null when
// nothing is known, in which case Send now is disabled and this is unreachable.
function destinationSentence(o, card) {
  const d = card && card.destination;
  if (d && d.kind === 'contact-on-file') {
    const who = (card.recipient && card.recipient.name) || 'the recipient';
    const word = d.channel === 'email' ? 'email' : 'fax';
    return { lead: `Will ${word} to ${who}'s ${word} on file (`, value: d.value, tail: ')' };
  }
  if (d && (d.kind === 'dictated-fax' || d.kind === 'dictated-email')) {
    return { lead: `Will ${d.channel === 'email' ? 'email' : 'fax'} to `, value: d.value, tail: '' };
  }
  const text = destinationText(o);
  if (!text) return null;
  return { lead: `Will ${o.recipient_email && !o.recipient_fax ? 'email' : 'fax'} to `, value: text, tail: '' };
}

// The recipient line under a referral's detail (L-CF-6: the card has never shown
// one). Names the contact that will receive the letter, says a NEW contact will
// be created when they confirm, or says nobody was found and the draft is going
// to the placeholder — never a dictated word dressed up as a resolved entity.
function recipientLine(card) {
  if (!card || card.type !== 'referral') return null;
  const spoken = (card.dictated && card.dictated.recipient) || null;
  const d = card.destination || { kind: 'none' };
  const word = d.channel === 'email' ? 'email' : 'fax';
  // PR-CF-4: the name matched several contacts and the provider is choosing —
  // nothing is chosen yet, and the draft is not addressed to the placeholder.
  // The count is of the contacts that match (the card's options, the "New
  // contact" one aside, plus any the cap of eight left off).
  if (card.recipientStatus === 'choose') {
    const opts = Array.isArray(card.recipientOptions) ? card.recipientOptions : [];
    const n = opts.filter((o) => o && o.kind !== 'new').length + (Number(card.recipientOptionsTruncated) || 0);
    return `To: not chosen yet — ${n === 1 ? '1 contact matches' : `${n} contacts match`} "${spoken || 'the recipient you named'}"; pick one below`;
  }
  if (card.recipientStatus === 'new') {
    const name = spoken || (card.recipient && card.recipient.name) || 'the recipient you named';
    const ep = d.value ? ` — ${word} ${d.value}` : '';
    return `To: new contact "${name}"${ep}, created when you confirm`;
  }
  if (card.recipientStatus === 'resolved') {
    const name = (card.recipient && card.recipient.name) || 'the recipient';
    if (d.kind === 'dictated-fax' || d.kind === 'dictated-email') return `To: ${name} · will ${word} to ${d.value}`;
    if (d.kind === 'contact-on-file') return `To: ${name} · ${word} on file (${d.value})`;
    return `To: ${name} · no fax or email on file — set one to send`;
  }
  const phone = card.phoneOnly ? ' A phone number can\'t receive a fax — set a fax or email to send.' : '';
  const head = spoken ? `No recipient found for "${spoken}"` : 'No recipient chosen';
  return `${head} — this draft will be addressed to a placeholder with an unusable fax;`
    + ` set the recipient in Elation before signing.${phone}`;
}

function orderDetail(o, card) {
  if (o.type === 'medication') {
    // The DTO first, for the same reason the title reads it (L70): the dose,
    // the quantity and the refills a pick rewrote live there and nowhere else.
    const med = (card && card.type === 'medication')
      // `directions` is what `_cardFields` spells the sig as, and it is always
      // set where the order has a frequency at all — so the `||` below can only
      // reach the order for a card that states neither, never to resurrect a
      // value the DTO has replaced.
      ? { dose: card.dose, freq: card.directions || o.frequency || o.directions, qty: card.qty,
        units: card.units, refills: card.refills, dx: card.dx }
      : { dose: o.dose, freq: o.frequency || o.directions, qty: o.qty,
        units: o.qty_units, refills: o.refills, dx: o.dxLabel };
    const parts = [];
    if (med.dose) parts.push(med.dose);
    if (med.freq) parts.push(med.freq);
    if (med.qty) parts.push(`#${med.qty}${med.units ? ' ' + med.units.toLowerCase() : ''}`);
    if (med.refills != null) parts.push(`${med.refills} refills`);
    if (med.dx) parts.push('for ' + med.dx);
    return parts.join(' · ');
  }
  const testList = Array.isArray(o.tests) ? o.tests : (Array.isArray(o.quest_tests) ? o.quest_tests : null);
  if (testList && testList.length) {
    // tests may be strings (lab/imaging) or objects (quest_homebound) — show names.
    const base = testList.map(t => (t && typeof t === 'object' ? t.name : t)).filter(Boolean).join(', ');
    return o.dxLabel ? `${base} · for ${o.dxLabel}` : base;
  }
  return o.dxLabel || '';
}

function OrderCard({ order, draftId, card, onEdit, onSubmit, onPreview, onSend, sent, patient, rowLive, control, orderIndex = null, question = null, onPick }) {
  // Local "Send now" confirm + in-flight guard for the generic recipient-routed
  // footer (referrals + facility-routed diagnostics). confirming = the inline
  // "Will fax/email to … · Confirm / Cancel" is showing; busy gates re-entry so a
  // double-click can't double-submit. (Hooks declared before the early return —
  // the lab/quest branch renders LabOrderCard which owns its own state.)
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  // Lab + Quest homebound render as the document-faithful card (Task 9) when a
  // matching resolution card is present. Everything else keeps the summary card.
  if (card && (card.type === 'lab' || card.type === 'quest_homebound')) {
    return <LabOrderCard card={card} draftId={draftId} onEdit={onEdit} onSubmit={onSubmit} onPreview={onPreview} onSend={onSend} sent={sent} rowLive={rowLive} control={control} />;
  }

  // Recipient-routed gate (matches the DestinationEditor below): referrals
  // (always recipient-bound) + facility-routed diagnostics carrying a facility/
  // recipient. These get the Save draft / Send now footer.
  const recipientRouted = order.type === 'referral'
    || (['imaging', 'cardiac', 'pulmonary', 'sleep'].includes(order.type)
        && (order.send_to_facility || order.recipient_fax || order.recipient_email));
  const dest = destinationText(order);
  // R-CF-1: what the card says about the recipient, and whether a transmit is
  // possible at all. The server refuses the same send at the gate.
  const toLine = recipientLine(card);
  const canSend = sendReadyFor(order, card);
  const willSend = destinationSentence(order, card);

  async function runSend(mode) {
    if (busy || !onSend) return;
    setBusy(true);
    try { await onSend(mode); } finally { setBusy(false); setConfirming(false); }
  }

  return (
    <div style={{
      marginTop: 6,
      padding: '8px 12px',
      background: '#fafafa',
      border: '1px solid #e5e7eb',
      borderRadius: 10,
      fontSize: 13,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 18 }}>{orderIcon(order.type)}</span>
        <span style={{ fontWeight: 600 }}>{orderTitle(order, card)}</span>
        {/* L-CF-17: a GUESSED specialty was spoken in the readback and rendered
            nowhere — the card said "Referral to Cardiology" as if the provider
            had said Cardiology. R-CF-1: say so on every surface. */}
        {card && card.type === 'referral' && card.specialtyFrom && (
          <span style={{ fontSize: 12, color: '#6b7280' }}>{`(closest match for "${card.specialtyFrom}")`}</span>
        )}
        {showPickChip(card) && <PickProductChip card={card} />}
        {draftId != null && (
          <span style={{
            marginLeft: 'auto', fontFamily: 'IBM Plex Mono, monospace',
            fontSize: 11, color: '#0f766e', background: '#ecfdf5',
            padding: '2px 6px', borderRadius: 4,
          }}>
            Elation #{draftId}
          </span>
        )}
      </div>
      {orderDetail(order, card) && (
        <div style={{ marginTop: 4, color: '#555' }}>{orderDetail(order, card)}</div>
      )}
      {/* Who the letter goes to. A referral card has never shown this (L-CF-6),
          so a provider confirmed a card that named no recipient at all. */}
      {toLine && (
        <div style={{ marginTop: 4, color: canSend ? '#374151' : '#92400e' }}>{toLine}</div>
      )}
      {/* This order's own disclosures (allergy, coverage, a substituted
          destination) — the card used to show none of them. Reads the card DTO
          when the resolution staged one, else the extracted order itself. */}
      <OrderWarnings lines={warningLines(card || order)} />
      {/* R-RX-14: the product slot — the choices this row itself holds. PR-CF-4:
          an OPEN slot answers the open question through `/pick` (the view's own
          option, named by its key), and only on the card the question is about;
          O113's resolved-card switch keeps its `/order-edit` action. */}
      <MedProductSlot card={card} onEdit={onEdit} question={question} orderIndex={orderIndex} onPick={onPick} />
      {/* PR-CF-4: a referral whose recipient matched several contacts — the
          choices on the card, the same door. */}
      <RecipientSlot card={card} question={question} orderIndex={orderIndex} onPick={onPick} />
      {/* Editable Send-to destination for recipient-routed orders that render in
          this generic branch: referrals (always recipient-bound) and facility-
          routed diagnostics (imaging/cardiac/pulmonary/sleep) when they carry a
          facility/recipient. Quest + plain e-Req labs are handled in LabOrderCard;
          medications + recipient-less diagnostics render their summary only. The
          server `set-destination` action accepts all these types. */}
      {(order.type === 'referral'
        || (['imaging', 'cardiac', 'pulmonary', 'sleep'].includes(order.type)
            && (order.send_to_facility || order.recipient_fax || order.recipient_email))) && (
        <>
          <DestinationEditor
            value={{ fax: order.recipient_fax, email: order.recipient_email }}
            onSet={(channel, value) => onEdit && onEdit({ action: 'set-destination', payload: { channel, value } })}
          />
          {/* Why "Send now" is disabled, said where the fix is (veto (iii)/(viii)). */}
          {!canSend && !sent && (
            <div style={{ marginTop: 4, fontSize: 12, color: '#92400e' }}>set a fax or email to send</div>
          )}
        </>
      )}

      {/* Footer actions for recipient-routed cards: Save draft (unsigned Elation
          draft, the existing behavior) + Send now (sign + transmit). Send now is
          a two-step inline confirm that shows the destination first — there's no
          requisition PDF to preview (unlike Quest), so the confirm IS the
          destination read. Once dispatched, the card flips read-only. */}
      {/* A marked slot shows its badge whatever the card type: a medication or a
          recipient-less diagnostic never enters the recipient-routed branch, and
          the scalar completion turn that used to carry the id is deliberately no
          longer drawn under a batch — so gating the badge on routing and on a
          live handler lost the id altogether (codex, R-S5-48). The unsent action
          controls keep their own gate, below. */}
      {/* O120: EVERY card carries a Confirm. Before this only a recipient-routed
          card had a footer at all — a medication's only confirm was the chat
          "yes" this client no longer types — and the footer's button was never
          told whether the server would accept it. `control` is the server's own
          answer (R-S5-52 / D4): `enabled` from the confirm gate's predicates,
          and `reason` is the gate's own sentence, shown as the tooltip rather
          than discovered as a 409. */}
      {(sent || ((recipientRouted || control) && onSend)) && (
        sent ? (
          <div style={{
            marginTop: 10, paddingTop: 10, borderTop: '1px solid #eef2f7',
            display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center',
            color: '#0f766e', fontSize: 13, fontWeight: 600,
          }}>
            {sent.sent
              ? `✓ ${dest ? `faxed/emailed to ${dest}` : 'sent in Elation'}`
              : `✓ drafted in Elation${sent.elationId != null ? ` #${sent.elationId}` : ''}`}
          </div>
        ) : confirming ? (
          <div style={{
            marginTop: 10, paddingTop: 10, borderTop: '1px solid #eef2f7',
          }}>
            {/* O73 / R-S5-9: the moment PHI actually leaves the building is the
                one that needs the subject named. Under a multi-patient batch
                every card belongs to a different chart, so the destination
                confirm says WHOSE order it is — name plus the same spoken DOB
                token the readback and the card header use (dobSpoken, straight
                off the DTO, never re-derived here). */}
            {patient && (
              <div style={{ fontSize: 12, color: '#111', marginBottom: 4, fontWeight: 600 }}>
                {`${patient.firstName || ''} ${patient.lastName || ''}`.trim()}
                {patient.dobSpoken ? <span style={{ fontWeight: 400, color: '#6b7280' }}> · DOB {patient.dobSpoken}</span> : null}
              </div>
            )}
            {/* R-CF-1 / veto (iv): the confirm NAMES where the letter goes. "Will
                sign and transmit this order in Elation" is not a destination —
                it was what the card said whenever nothing had been dictated,
                which is precisely the case where nobody knew where it would go.
                Send now is disabled unless a destination is known, so there is
                no state left in which this has nothing to name. */}
            <div style={{ fontSize: 12, color: '#374151', marginBottom: 8 }}>
              {willSend && <>{willSend.lead}<b>{willSend.value}</b>{willSend.tail}</>}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                disabled={busy}
                onClick={() => setConfirming(false)}
                style={{
                  padding: '7px 14px', fontSize: 13, fontWeight: 600,
                  color: '#374151', background: '#fff',
                  border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => runSend('send')}
                style={{
                  padding: '7px 14px', fontSize: 13, fontWeight: 600,
                  color: '#fff', background: busy ? '#9ca3af' : '#0f766e',
                  border: 'none', borderRadius: 8, cursor: busy ? 'wait' : 'pointer',
                }}
              >
                {busy ? 'Sending…' : 'Confirm'}
              </button>
            </div>
          </div>
        ) : (
          <div style={{
            marginTop: 10, paddingTop: 10, borderTop: '1px solid #eef2f7',
            display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center',
          }}>
            {control && control.enabled === false && control.reason && (
              <span style={{ marginRight: 'auto', fontSize: 12, color: '#92400e' }}>{control.reason}</span>
            )}
            <button
              type="button"
              disabled={busy || (control ? control.enabled === false : false)}
              title={control && control.enabled === false ? control.reason : undefined}
              // Pinned by IDENTITY, not by counting the word: this page already
              // renders a "Confirm" on the Send-now destination dialog and a
              // "Confirm & fax" on the Quest footer, so `>Confirm<` says nothing
              // about WHICH control a test is looking at (P1 departure 3, now
              // reversed: the plan's label is back).
              data-action="confirm-card"
              onClick={() => runSend('draft')}
              style={{
                padding: '7px 14px', fontSize: 13, fontWeight: 600,
                color: '#374151', background: '#fff',
                border: '1px solid #d1d5db', borderRadius: 8,
                cursor: control && control.enabled === false ? 'not-allowed' : 'pointer',
              }}
            >
              Confirm
            </button>
            {recipientRouted && (
              <button
                type="button"
                disabled={busy || !canSend || (control ? control.enabled === false : false)}
                title={canSend ? (control && control.enabled === false ? control.reason : undefined) : 'set a fax or email to send'}
                onClick={() => setConfirming(true)}
                style={{
                  padding: '7px 14px', fontSize: 13, fontWeight: 600,
                  color: '#fff', background: canSend ? '#0f766e' : '#9ca3af',
                  border: 'none', borderRadius: 8, cursor: canSend ? 'pointer' : 'not-allowed',
                }}
              >
                Send now
              </button>
            )}
          </div>
        )
      )}
    </div>
  );
}

// One turn's cards, as GROUPS (O64). A multi-patient dictation answers with one
// entry per patient — each entry an ordinary single-patient card set with its
// own sessionId — so the group is the unit the handlers key on. A single-patient
// turn is one group with entryIndex null, i.e. exactly today's shape: the same
// turn.sessionId, the same turn.orders, the same turn.resolution.
// R-CF-7 / D4: what a turn's card may do is the SERVER's answer, carried on the
// reply as `view.controls` and computed from the confirm gate's own predicates.
// The page used to derive it — "does any card read `productStatus:'unresolved'`?"
// — which is a second copy of a server rule, and a second copy drifts: the
// heuristic knew about an unresolved PRODUCT and nothing about an unmatched lab
// line, a dangling clarify, or a half-sent card's remaining orders, all of which
// the gate reads. `turnHasUnresolvedCard` is gone with it.
//
// A turn carrying no view at all is a reply from before this slice; the control
// renders as it always did and the route's own gate is the backstop it has
// always been.
// ONE LIVE VIEW PER SESSION (R-CF-7 / D3). A view is not a property of the turn
// it arrived on — it is the server's current account of a CONVERSATION, and the
// page may hold several turns about the same one. Keying it on the turn meant a
// card rendered at v1 kept confirming at v1 while every later reply moved the
// row on: after an edit, after a per-card confirm, and on every History or
// voice-memo resume, the O120 flow answered 409 or 400 and the provider could
// not file the order at all.
//
// So every reply and every refusal that carries a view writes it here, and every
// reader looks here FIRST. The turn's own snapshot stays as the fallback for a
// reply from before this slice.
function viewsFromReply(prev, data) {
  if (!data) return prev || {};
  const incoming = [];
  if (data.view && data.view.sessionId != null) incoming.push(data.view);
  // A batch answers with one view per SIBLING; each card confirms against its own.
  if (Array.isArray(data.views)) for (const v of data.views) if (v && v.sessionId != null) incoming.push(v);
  if (!incoming.length) return prev || {};
  const next = { ...(prev || {}) };
  for (const v of incoming) next[String(v.sessionId)] = v;
  return next;
}
function viewOfTurn(views, turn, sessionId) {
  const want = sessionId == null ? null : String(sessionId);
  if (!want) return null;
  const live = views && views[want];
  if (live) return live;
  if (!turn) return null;
  if (Array.isArray(turn.views)) {
    const v = turn.views.find((x) => x && String(x.sessionId) === want);
    if (v) return v;
  }
  if (turn.view && String(turn.view.sessionId) === want) return turn.view;
  return null;
}
// The view for the conversation a TURN is about — its own view's session when it
// has one (a receipt-built turn carries no `sessionId` of its own), else the
// session the turn names.
function turnViewOf(views, turn) {
  const sid = (turn && turn.view && turn.view.sessionId) || (turn && turn.sessionId) || null;
  return sid == null ? null : viewOfTurn(views, turn, sid);
}
function confirmControlOf(views, turn, sessionId, orderIndex) {
  const v = viewOfTurn(views, turn, sessionId);
  const list = v && v.controls && Array.isArray(v.controls.confirm) ? v.controls.confirm : null;
  if (!list) return null;
  return list.find((c) => c && c.orderIndex === orderIndex)
    || { orderIndex, enabled: false, reason: 'this order card is no longer awaiting confirmation' };
}
// The WHOLE-SESSION control ("Send all" — the chat "yes" this client no longer
// sends). Absent under a live batch by the server's own rule: one word cannot
// pick among charts.
function sessionConfirmEnabled(views, turn) {
  const v = turnViewOf(views, turn);
  const list = v && v.controls && Array.isArray(v.controls.confirm) ? v.controls.confirm : null;
  if (!list) return true;
  const whole = list.find((x) => x && x.orderIndex === null);
  return !!(whole && whole.enabled);
}
// …and when it is the ONLY confirm door this conversation has. A practice
// running the default flags has no per-card `/order-send` — the route refuses it
// — so the server marks every per-card entry disabled and leaves the whole-
// session one enabled. The button used to render only for a turn holding MORE
// THAN ONE order, which left such a practice able to stage an order and never
// file it (ADJ-1). A half-sent card resumed from History is the same shape.
function sessionConfirmOnly(turn, views) {
  const v = turnViewOf(views, turn);
  const list = v && v.controls && Array.isArray(v.controls.confirm) ? v.controls.confirm : null;
  if (!list) return false;
  const whole = list.find((x) => x && x.orderIndex === null);
  if (!whole || !whole.enabled) return false;
  // An enabled per-card control is only a DOOR if the provider can see it, and it
  // is drawn on a card. A RESUMED half-sent conversation draws none —
  // `_sessionCardPayload` builds a `resolution` only at `awaiting_confirmation`
  // or a med-only picker, so `partially_sent` comes back cardless — while the
  // view still offers a Confirm on the order that is left. Reading "is any
  // per-card entry enabled?" therefore withheld the only door such a
  // conversation had, on the very path the memo hand-off and every History click
  // take, while the chat "yes" that used to finish it is refused (veto (xxi)).
  //
  // So the question is whether an enabled control has somewhere to appear.
  const drawn = new Set();
  for (const g of groupsOfTurn(turn)) {
    if (g && (g.sessionId == null || String(g.sessionId) === String(v.sessionId))) {
      (g.orders || []).forEach((_, i) => drawn.add(i));
    }
  }
  return !list.some((x) => x && x.orderIndex != null && x.enabled && drawn.has(x.orderIndex));
}

function groupsOfTurn(turn) {
  // The states in which a sibling is still being ASKED about. It has no card by
  // construction (toBatchEntries admits only confirmation-ready rows) — and
  // before this it had nothing on screen at all, so a two-patient dictation
  // whose second patient was still a question looked like a one-patient one.
  const ASKING = ['awaiting_patient', 'awaiting_selection', 'awaiting_clarification', 'awaiting_med_details'];
  // `turn.batch` being an ARRAY is what makes this turn a batch — its LENGTH is
  // just how many cards are left. Requiring length meant an all-cancelled batch
  // fell through to the scalar branch below and resurrected entry 0's orders as
  // an unheadered card with a live Save draft, one click from a 409 (codex).
  // A single-patient turn never carries `batch` at all, so its branch is
  // reached exactly as before.
  if (Array.isArray(turn.batch)) {
    // `state` is the sibling's own ROW state as the roster last stated it —
    // read by exactly one thing (a still-live requisition may be re-faxed from
    // its card; a completed one may not, the route would 409). Null when the
    // turn predates the roster.
    const cardGroup = (e, i, state) => ({
      key: e.sessionId || `b${i}`,
      entryIndex: i,
      sessionId: e.sessionId,
      patient: e.patient || null,
      orders: e.extractedOrders || [],
      resolution: e.resolution || null,
      state: state || null,
    });
    // The roster the server states for every sibling (R-S5-46) is what puts the
    // batch in batch_seq order and what makes the card-less siblings visible. A
    // turn rendered before the roster existed still renders its cards.
    const roster = Array.isArray(turn.sessions) ? turn.sessions : null;
    if (!roster) return turn.batch.map((e, i) => cardGroup(e, i));
    const indexOf = new Map(turn.batch.map((e, i) => [String(e.sessionId), i]));
    const out = [];
    const seen = new Set();
    for (const s of roster) {
      if (!s || s.sessionId == null) continue;
      const id = String(s.sessionId);
      seen.add(id);
      // An open question, or a dispatch in flight, outranks whatever card this
      // turn is still holding for that sibling: in both states every control on
      // that card is refused by the server anyway.
      //
      // R-RX-14 is the exception the server states for itself: an UNRESOLVED
      // MEDICATION is still an open question, but it has a card — the server
      // built an entry for it, carrying the picker's question in the prompt and
      // the choices in the slot. Where there is an entry, the card is what the
      // provider needs to see; where there is not (a lab pick, a dx pick), the
      // placeholder stands exactly as before.
      const hasEntry = indexOf.has(id);
      if ((ASKING.includes(s.state) && !(s.state === 'awaiting_selection' && hasEntry))
          || s.state === 'processing') {
        out.push({
          key: `x${id}`,
          entryIndex: null,
          sessionId: s.sessionId,
          patient: s.patient || null,
          orders: [],
          resolution: null,
          placeholder: s.state === 'processing' ? 'processing' : 'question',
        });
        continue;
      }
      // A confirmable sibling renders its card; a COMPLETED one keeps the card
      // it already had, for its badge. Nothing NEW is drawn for a finished,
      // cancelled or expired sibling — a cancelled entry has already left
      // `turn.batch` by then (lib/order-batch).
      if (indexOf.has(id)) out.push(cardGroup(turn.batch[indexOf.get(id)], indexOf.get(id), s.state));
    }
    // A sibling the roster does not mention is left exactly as it was rendered.
    turn.batch.forEach((e, i) => { if (!seen.has(String(e.sessionId))) out.push(cardGroup(e, i)); });
    return out;
  }
  if (Array.isArray(turn.orders) && turn.orders.length) {
    return [{ key: 's', entryIndex: null, sessionId: turn.sessionId, patient: null, orders: turn.orders, resolution: turn.resolution || null }];
  }
  return [];
}
// A group's slot key: the SESSION plus the order index for a batch card (a
// position in `resolutions` shifts as patients finish — see lib/order-batch),
// and the bare order index for a single-patient turn, unchanged.
const groupSlot = (group, orderIndex) => cardSlot(group.entryIndex == null ? null : group.sessionId, orderIndex);

// The patient line a batch card wears above its header. Both tokens come off
// the DTO — `dobSpoken` IS formatDob, the exact string the readback speaks — so
// the card and the speech can never name the same patient two ways (#634).
function PatientCardHeader({ patient }) {
  const name = `${patient.firstName || ''} ${patient.lastName || ''}`.trim();
  if (!name) return null;
  return (
    <div style={{
      marginTop: 10, marginBottom: 2, display: 'flex', alignItems: 'baseline', gap: 8,
      fontSize: 13, fontWeight: 700, color: '#111',
    }}>
      <span>{name}</span>
      {patient.dobSpoken && (
        <span style={{ fontWeight: 400, fontSize: 12, color: '#6b7280' }}>· DOB {patient.dobSpoken}</span>
      )}
    </div>
  );
}

// A sibling of the batch with no card on this turn: its patient question or its
// pick is still open, or its orders are being filed right now. Deliberately a
// LINE, not a card — no controls, nothing "Send all" counts (it holds no
// orders), nothing a click can reach. Without it the batch renders as though
// that patient were not in it, which is how a two-patient dictation could show
// one card and no sign of the second patient at all.
function BatchPlaceholder({ kind, patient }) {
  return (
    <div style={{
      marginTop: 6, padding: '8px 12px',
      background: '#fafafa', border: '1px dashed #e5e7eb', borderRadius: 10,
      fontSize: 13, color: '#6b7280',
    }}>
      {!patient && (
        <div style={{ fontSize: 13, fontWeight: 700, color: '#111', marginBottom: 2 }}>Patient not yet identified</div>
      )}
      {kind === 'processing'
        ? 'Being sent right now — say undo once it finishes to retract.'
        : 'still needs an answer above'}
    </div>
  );
}

// One batch card, closed. The provider opens each one to review it — that
// gesture IS the review the confirm-all button waits for (O64), so the chevron
// is not decoration. The summary names only the order TITLES, never the tests
// inside them, which is precisely what the card behind it is for.
function batchCardSummary(group) {
  const titles = (group.orders || []).map(orderTitle).filter(Boolean);
  return titles.length ? titles.join(' · ') : 'No orders on this card';
}

function BatchCardToggle({ expanded, summary, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      style={{
        marginTop: 6, width: '100%', display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 12px', background: '#fafafa', border: '1px solid #e5e7eb',
        borderRadius: 10, fontSize: 13, color: '#374151', textAlign: 'left',
        cursor: 'pointer', fontFamily: 'inherit',
      }}
    >
      <span aria-hidden="true" style={{ color: '#6b7280' }}>{expanded ? '▾' : '▸'}</span>
      <span>{summary}</span>
    </button>
  );
}

// onEditOrder(entryIndex, orderIndex, edit) — bound by the page to this turn;
// opens/persists a per-test dx edit (Task 10). Only the resolution-bearing turn
// passes it. entryIndex is null for a single-patient turn.
// R-CF-7 / PR-CF-P2. A resumed thread used to open with a server-authored
// sentence about itself ("Resuming this conversation — it was waiting on your
// reply"). For a declaring client the server sends `replies: []` instead and the
// conversation's state arrives as a FACT on the view; this is the whole of the
// client's vocabulary for it — a fixed lookup, never a claim about what was
// written (R-RX-10). An unknown state renders nothing at all.
const CARD_STATE_LABELS = {
  awaiting_confirmation: 'Waiting on your reply',
  awaiting_selection: 'Waiting on your reply',
  awaiting_patient: 'Waiting on your reply',
  awaiting_med_details: 'Waiting on your reply',
  awaiting_clarification: 'Waiting on your reply',
  partially_sent: 'Partly filed — confirm the rest',
  completed: 'Completed',
  cancelled: 'Cancelled',
  expired: 'Expired',
};
function stateLabelOf(view) {
  return (view && CARD_STATE_LABELS[view.state]) || null;
}

function Bubble({ turn, views, onEditOrder, onSubmitOrder, onPreviewOrder, onSendOrder, onSendAll, onCancelCard, onUndoCard, onToggleCard, onRetry, onPickOption, compact = false }) {
  // The SERVER's current account of this conversation — not the snapshot the
  // turn arrived with (R-CF-7 / D3).
  const liveView = turnViewOf(views, turn);
  const me = turn.from === 'me';
  // Everything below keys on the turn being a BATCH — never on how many orders
  // it holds. The single-patient turn that staged two orders keeps its "Send
  // all" button, its always-expanded cards and its byte-identical markup.
  const batchTurn = isBatchTurn(turn);
  const opened = batchTurn ? openedCount(turn) : 0;
  const cardsOnTurn = batchTurn ? confirmableSessionIds(turn).length : 0;
  const reviewed = batchTurn ? allOpened(turn) : false;
  // Several turns of one batch are on screen at once, so the hint the disabled
  // button points at needs an id of its own per bubble, not per batch.
  const hintId = `oc-open-hint-${useId()}`;
  const outcomes = (turn.outcomes && turn.outcomes.bySession) || null;
  return (
    <div className="orderchat-bubble-in" style={{
      alignSelf: me ? 'flex-end' : 'flex-start',
      // `compact`: one card drawn INSIDE a refill sheet's line (PR-E) — the sheet is the
      // bubble, so the card takes the line's width and carries no timestamp of its own.
      maxWidth: compact ? '100%' : '85%',
    }}>
      {/* A turn with no TEXT is a card-bearing turn: the server said what it
          had to say on the card (the question's prompt, the controls' prompt,
          the state label) and sent no sentence to print beside it. Drawing an
          empty speech bubble for it would be drawing a claim nobody made. */}
      {(turn.text || turn.error) ? (
      <div role={turn.error ? 'alert' : undefined} style={{
        background: turn.error ? '#fee2e2' : me ? '#0f766e' : '#ffffff',
        color: turn.error ? '#991b1b' : me ? '#ffffff' : '#111',
        border: me || turn.error ? 'none' : '1px solid #e5e7eb',
        borderRadius: 16,
        borderBottomRightRadius: me ? 4 : 16,
        borderBottomLeftRadius: me ? 16 : 4,
        padding: '10px 14px',
        fontSize: 14,
        lineHeight: 1.45,
        whiteSpace: 'pre-wrap',
        boxShadow: me ? 'none' : '0 1px 2px rgba(0,0,0,0.04)',
      }}>
        {turn.text}
        {turn.error && onRetry && (
          <div style={{ marginTop: 8 }}>
            <button className="btn ghost sm" onClick={onRetry}>Try again</button>
          </div>
        )}
      </div>
      ) : (stateLabelOf(liveView) && (
        <div data-role="card-state-label" style={{
          fontSize: 12, fontWeight: 600, color: '#6b7280', padding: '2px 2px 6px',
        }}>
          {stateLabelOf(liveView)}
        </div>
      ))}
      {groupsOfTurn(turn).length > 0 && (
        <div style={{ marginTop: 4 }}>
          {/* What the whole confirm did, in counts. The per-patient lines are on
              the cards below and the same report is in the thread as a bubble;
              this is the one line that says how the batch as a whole came out. */}
          {turn.outcomes && turn.outcomes.summary && (
            <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
              {turn.outcomes.summary}
            </div>
          )}
          {groupsOfTurn(turn).map((group) => {
            // A batch card is collapsible; a single-patient turn's card and a
            // question placeholder are not, and render exactly as before.
            const collapsible = batchTurn && group.entryIndex != null && !group.placeholder;
            const expanded = !collapsible || isExpanded(turn, group.sessionId);
            const outcome = (outcomes && group.sessionId != null && outcomes[String(group.sessionId)]) || null;
            return (
            <div key={group.key}>
              {group.patient && <PatientCardHeader patient={group.patient} />}
              {/* What became of THIS patient — keyed by her session, never by
                  where her card sits (the array shrinks as patients finish). */}
              {outcome && (
                <div style={{
                  marginTop: 4, fontSize: 12, fontWeight: 600,
                  color: outcome.kind === 'filed' ? '#0f766e' : '#b45309',
                }}>
                  {outcome.line}
                </div>
              )}
              {/* The outcome line says the same thing the placeholder does and
                  says what the confirm did about it, so the two never stack. */}
              {group.placeholder && !outcome && <BatchPlaceholder kind={group.placeholder} patient={group.patient} />}
              {collapsible && (
                <BatchCardToggle
                  expanded={expanded}
                  summary={batchCardSummary(group)}
                  onToggle={onToggleCard ? () => onToggleCard(group.sessionId) : undefined}
                />
              )}
              {expanded && group.orders.map((order, i) => {
                const draft = (turn.orderIds || []).find(o => o.type === order.type);
                // extractedOrders / resolution.cards are index-aligned (both
                // orders.map(...)), so this order's position IS its orderIndex and
                // its resolution card — match by index, not by .type (multiple
                // orders can share a type).
                const cards = (group.resolution && group.resolution.cards) || [];
                const card = cards[i] || cards.find(c => c.type === order.type);
                const e = group.entryIndex;
                const slot = groupSlot(group, i);
                // PR-CF-4: the LIVE view of THIS card's conversation — its open
                // question is what the card's slot answers, and its pick names
                // that conversation, that question and that version (R-CF-7).
                const cardView = viewOfTurn(views, turn, group.sessionId);
                return (
                  <OrderCard
                    key={i}
                    order={order}
                    orderIndex={i}
                    question={cardView ? cardView.question : null}
                    onPick={onPickOption && cardView ? (option) => onPickOption(cardView, option) : undefined}
                    draftId={draft?.elationId}
                    card={card}
                    // R-S5-49: the Send-now confirm names its subject on a
                    // single-patient card too. A batch card takes the name from
                    // its own roster entry; a single-patient card has always had
                    // the same DTO sitting in its resolution and simply was not
                    // asked for it. Both tokens still come off the DTO — never
                    // re-derived here (#634). A DELIBERATE flag-independent
                    // change: a referral in a flag-off practice now names the
                    // patient at its destination confirm, which is the moment
                    // O73 puts the readback at.
                    patient={group.patient
                      || (group.resolution && group.resolution.patients && group.resolution.patients[0])
                      || null}
                    // Only a row that is still LIVE may be re-sent from its
                    // card; a completed one is refused by the route (409).
                    rowLive={group.state === 'partially_sent'}
                    onEdit={onEditOrder ? (edit) => onEditOrder(e, i, edit) : undefined}
                    onSubmit={onSubmitOrder ? (arg) => onSubmitOrder(e, i, arg) : undefined}
                    onPreview={onPreviewOrder ? () => onPreviewOrder(e, i) : undefined}
                    onSend={onSendOrder ? (mode) => onSendOrder(e, i, mode) : undefined}
                    sent={(turn.sentOrders && turn.sentOrders[slot]) || null}
                    // R-CF-7 / D4: whether THIS card may be confirmed right now,
                    // and the gate's own sentence when it may not.
                    control={confirmControlOf(views, turn, group.sessionId, i)}
                  />
                );
              })}
            </div>
            );
          })}
        </div>
      )}
      {onSendAll
        && (sessionConfirmOnly(turn, views)
          || (groupsOfTurn(turn).reduce((n, g) => n + g.orders.length, 0) > 1
            && groupsOfTurn(turn).some((g) => g.orders.some((_, i) => !(turn.sentOrders && turn.sentOrders[groupSlot(g, i)])))))
        && (batchTurn || sessionConfirmEnabled(views, turn)) && (
        /* O64: over a BATCH the button says what it confirms and waits until
           every card has been opened — the hint sits beside it and the button
           points at it, so the disabled state is not silent to a screen reader.
           The single-patient branch below is the turn that staged two orders:
           this is keyed on the turn being a BATCH, never on the order count, so
           its button is the one it has always been. */
        batchTurn ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 8 }}>
            {!reviewed && (
              <span id={hintId} style={{ fontSize: 12, color: '#6b7280' }}>{OPEN_EACH_CARD_HINT}</span>
            )}
            <button
              type="button"
              onClick={onSendAll}
              disabled={!reviewed}
              aria-describedby={reviewed ? undefined : hintId}
              style={{
                padding: '7px 14px', fontSize: 13, fontWeight: 600,
                color: reviewed ? '#0f766e' : '#9ca3af', background: '#fff',
                border: `1px solid ${reviewed ? '#0f766e' : '#d1d5db'}`,
                borderRadius: 8, cursor: reviewed ? 'pointer' : 'not-allowed',
              }}
            >
              {`Confirm all reviewed cards (${opened} of ${cardsOnTurn} opened)`}
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <button
              type="button"
              onClick={onSendAll}
              style={{
                padding: '7px 14px', fontSize: 13, fontWeight: 600,
                color: '#0f766e', background: '#fff',
                border: '1px solid #0f766e', borderRadius: 8, cursor: 'pointer',
              }}
            >
              Send all
            </button>
          </div>
        )
      )}
      {/* Cancel and Undo, from the server's own controls (R-CF-7 / D4). "no" and
          "undo" were words typed into the composer, which this client no longer
          speaks — and "undo" retracted "the most recent completed session" on a
          key the web, the phone and every memo hand-off share. Both name their
          conversation now. Offered exactly where the server accepts them: Cancel
          while the card is pending, Undo on a finished one that has drafts to
          retract. */}
      {/* What the card's controls will do — the server's own sentence, moved out
          of the transcript by the renderer seam (PR-CF-P2) so the readback above
          is not followed by a line telling the provider to say a word this page
          no longer sends. */}
      {liveView && (liveView.controls || {}).prompt && (
        <div data-role="controls-prompt" style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
          {liveView.controls.prompt}
        </div>
      )}
      {liveView && (liveView.controls || {}).cancel && onCancelCard && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
          <button
            type="button"
            onClick={() => onCancelCard(liveView.sessionId)}
            style={{
              padding: '7px 14px', fontSize: 13, fontWeight: 600,
              color: '#374151', background: '#fff',
              border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        </div>
      )}
      {liveView && (liveView.controls || {}).undo && onUndoCard && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
          <button
            type="button"
            onClick={() => onUndoCard(liveView.sessionId)}
            style={{
              padding: '7px 14px', fontSize: 13, fontWeight: 600,
              color: '#374151', background: '#fff',
              border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer',
            }}
          >
            Undo
          </button>
        </div>
      )}
      {!compact && (
      <div style={{
        fontSize: 11, color: '#9ca3af', marginTop: 4,
        textAlign: me ? 'right' : 'left',
      }}>
        {timeStr(turn.time)}
      </div>
      )}
    </div>
  );
}

// Web Speech API wrapper — graceful no-op on browsers without support
function useSpeechInput(onResult) {
  const Recog = typeof window !== 'undefined'
    ? (window.SpeechRecognition || window.webkitSpeechRecognition)
    : null;
  const [listening, setListening] = useState(false);
  const recogRef = useRef(null);

  const start = useCallback(() => {
    if (!Recog) return;
    const r = new Recog();
    r.lang = 'en-US';
    r.interimResults = false;
    r.continuous = false;
    r.onresult = (e) => {
      const text = Array.from(e.results).map(res => res[0]?.transcript || '').join(' ').trim();
      if (text) onResult(text);
    };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    recogRef.current = r;
    setListening(true);
    try { r.start(); } catch { setListening(false); }
  }, [Recog, onResult]);

  const stop = useCallback(() => {
    if (recogRef.current) try { recogRef.current.stop(); } catch {}
    setListening(false);
  }, []);

  return { supported: !!Recog, listening, start, stop };
}

export function OrderChatPage({ resumeRequest, onResumeConsumed } = {}) {
  const [turns, setTurns] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [whoami, setWhoami] = useState(null);
  const [history, setHistory] = useState(null);
  // Explicit conversation pin after a resume (history click / voice-memo
  // hand-off): every subsequent send continues THAT session instead of the
  // server's "newest active" fallback. Cleared when the thread terminates.
  const [resumedSessionId, setResumedSessionId] = useState(null);
  // …and the same value where an in-flight reply can read it. A round trip that
  // resolves after the provider has clicked History must be judged against the
  // pin they hold NOW, not the one captured when the request went out — so the
  // pin is set through `pinSession` and never through the setter alone.
  const resumedSessionIdRef = useRef(null);
  function pinSession(id) {
    resumedSessionIdRef.current = id;
    setResumedSessionId(id);
  }
  const [historyOpen, setHistoryOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [savingPref, setSavingPref] = useState(false);
  const [starterDraft, setStarterDraft] = useState(null); // settings textarea; null until whoami loads
  const [savingStarters, setSavingStarters] = useState(false);
  const scrollerRef = useRef(null);
  const inputRef = useRef(null);
  const [pinnedPatient, setPinnedPatient] = useState(null); // {directoryId, firstName, lastName, dob}
  // Server-built chips for the CURRENT question ([{label, send}]) — numbered
  // picks / patient disambiguation / yes-no. Replaces the fixed row when set.
  const [dynamicReplies, setDynamicReplies] = useState([]);
  // R-CF-7 / D3: the server's structured view of the conversation this page is
  // currently about — its open question (with the ids that answer it), the
  // controls its state offers, and the version every confirm must name. The
  // page renders it; it derives nothing of its own from it.
  const [view, setView] = useState(null);
  // …and the LIVE view of every conversation this page has heard about, keyed by
  // session (R-CF-7 / D3). `view` above is the CURRENT conversation's — what the
  // composer's chip row answers; this map is what every CARD on screen renders
  // and confirms against, however many turns ago it was drawn. The ref is the
  // authority because the async handlers read it between renders; the state is
  // what makes the cards re-render when it moves.
  const [viewsBySession, setViewsBySession] = useState({});
  const viewsRef = useRef({});
  function recordViews(data) {
    const next = viewsFromReply(viewsRef.current, data);
    if (next === viewsRef.current) return;
    viewsRef.current = next;
    setViewsBySession(next);
  }
  const [suggestions, setSuggestions] = useState([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  // O83: -1 = nothing chosen. The picker never pre-selects a row, because Enter
  // in a message box means SEND and a pin the provider did not choose is a
  // subject-integrity input, not a label. Only ArrowUp/ArrowDown lift it off -1.
  const [activeIndex, setActiveIndex] = useState(-1);
  const searchSeqRef = useRef(0);
  const debounceRef = useRef(null);
  // Group-home refill sheets (PR-E): the upload dialog, and one poller per sheet on
  // screen (GET /api/sms/chat/roster/:id until it is read and staged).
  const [sheetDialogOpen, setSheetDialogOpen] = useState(false);
  const sheetPollersRef = useRef(new Map());
  // GPT-Live Talk mode (PR 2). `liveConfig` is /api/live/config's body when the
  // practice flag is ON (404 → null → no Talk button). `live` mirrors the client's
  // state machine for the pill; `liveCaptions` is the rail (stitched per speaker).
  const [liveConfig, setLiveConfig] = useState(null);
  const [live, setLive] = useState({ state: 'idle', phase: null, reason: null, seconds: null });
  const [liveCaptions, setLiveCaptions] = useState([]);
  const liveRef = useRef(null);            // the LiveClient for the current call
  const liveTurnsRef = useRef(new Map());  // delegationId → the turn frame's reply lines (reply-bubble dedupe)
  const liveRepliesRef = useRef(new Map()); // delegationId → { text, timer }: a reply held until its delegation ends
  const liveTimersRef = useRef(new Set()); // pending reply-bubble timers, cleared on unmount
  const liveErrorSeenRef = useRef(false);  // a FATAL error bubble already explains how the call ended
  const liveTokenRef = useRef(null);       // { token, expiresAt } — reused on a reconnect while it has life left
  const liveRailRef = useRef(null);        // the caption rail, kept scrolled to the newest line
  const ingestReplyRef = useRef(null);     // the client's callbacks outlive one render; they go through here

  useEffect(() => {
    ccmApi.getOrderChatWhoami().then(setWhoami).catch(() => setWhoami(null));
  }, []);

  // Talk mode is offered only when the server says the feature is ON (200); a 404
  // (flag OFF), a 403 (no orderchat permission) or any failure leaves it hidden.
  useEffect(() => {
    let alive = true;
    ccmApi.getLiveConfig()
      .then((cfg) => { if (alive) setLiveConfig(cfg && cfg.enabled && cfg.socketPath ? cfg : null); })
      .catch(() => { if (alive) setLiveConfig(null); });
    return () => { alive = false; };
  }, []);

  // NOTE: this block sits here, ahead of the page's handlers, on purpose — the
  // page-protocol and composer-keys suites slice the page from `function
  // ingestReply(` and from the composer handlers, and must not see it.
  // `ingestReply` is a function declaration (hoisted), so the ref below is valid.
  // ── GPT-Live Talk mode (PR 2) ────────────────────────────────────────────────
  //
  // The client (lib/live-client.js) owns the mic, the peer connection and the
  // control socket; this page wires four things and nothing more: a `turn` frame
  // IS the /send body and goes through ingestReply unchanged (the same cards,
  // views and chip rows as a typed order); a `reply` frame becomes a bot bubble
  // only when its delegation drew no card; the pin chip is forwarded as a `pin`
  // frame; End sends `end`. Errors are the page's own error bubbles, one plain
  // sentence per code.
  ingestReplyRef.current = ingestReply;

  function liveTimer(fn, ms) {
    const t = setTimeout(() => { liveTimersRef.current.delete(t); fn(); }, ms);
    liveTimersRef.current.add(t);
    return t;
  }

  // A `reply` is drawn as a bubble only if its delegation drew no card by the
  // time it ENDED (`thinking {active:false}`), or by LIVE_REPLY_WAIT_MS at most.
  function settleLiveReply(delegationId) {
    const held = liveRepliesRef.current.get(delegationId);
    if (!held) return;
    liveRepliesRef.current.delete(delegationId);
    if (held.timer) { clearTimeout(held.timer); liveTimersRef.current.delete(held.timer); }
    if (replyCoveredByTurn(liveTurnsRef.current, delegationId, held.text)) return;
    setTurns((t) => [...t, { from: 'bot', text: held.text, time: Date.now(), live: true }]);
  }

  function onLiveFrame(frame) {
    if (!frame || typeof frame.type !== 'string') return;
    if (frame.type === 'caption') {
      setLiveCaptions((c) => stitchCaption(c, frame, LIVE_CAPTION_MAX));
      return;
    }
    if (frame.type === 'turn') {
      // The envelope is ours; everything else is the /api/sms/chat/send body.
      const { type, delegationId, callId, tool, ...data } = frame; // eslint-disable-line no-unused-vars
      const seen = liveTurnsRef.current.get(delegationId) || [];
      liveTurnsRef.current.set(delegationId, seen.concat(Array.isArray(data.replies) ? data.replies : []));
      ingestReplyRef.current(data);
      return;
    }
    if (frame.type === 'reply') {
      const text = typeof frame.text === 'string' ? frame.text.trim() : '';
      if (!text) return;
      const id = frame.delegationId == null ? `reply-${Date.now()}` : frame.delegationId;
      const prev = liveRepliesRef.current.get(id);
      if (prev && prev.timer) { clearTimeout(prev.timer); liveTimersRef.current.delete(prev.timer); }
      const timer = liveTimer(() => settleLiveReply(id), LIVE_REPLY_WAIT_MS);
      liveRepliesRef.current.set(id, { text: prev ? `${prev.text} ${text}` : text, timer });
      return;
    }
    if (frame.type === 'thinking' && frame.active === false) {
      settleLiveReply(frame.delegationId);
      return;
    }
    if (frame.type === 'closed') {
      for (const id of [...liveRepliesRef.current.keys()]) settleLiveReply(id);
      if (!LIVE_QUIET_CLOSE.has(frame.reason) && !liveErrorSeenRef.current) {
        setTurns((t) => [...t, { from: 'bot', text: LIVE_CLOSE_TEXT[frame.reason] || 'Live voice ended unexpectedly.', time: Date.now(), error: true }]);
      }
    }
  }

  function onLiveError(err) {
    if (err && err.fatal) liveErrorSeenRef.current = true; // a non-fatal one must not silence the ending
    if (err && err.code === 'feature_off') setLiveConfig(null); // turned off under us: the Talk control goes with it (R2-F14)
    setTurns((t) => [...t, { from: 'bot', text: err.message || 'Live voice hit a problem.', time: Date.now(), error: true }]);
  }

  function onLiveState(s) {
    setLive((prev) => ({ state: s.state, phase: s.phase || null, reason: s.reason ?? (s.state === 'ended' ? prev.reason : null), seconds: s.seconds ?? null }));
  }

  async function startTalk() {
    if (!liveConfig || cannotOrder) return;
    if (liveRef.current && (liveRef.current.state === 'connecting' || liveRef.current.state === 'live')) return;
    if (speech.listening) speech.stop();
    liveTurnsRef.current = new Map();
    liveRepliesRef.current = new Map();
    liveErrorSeenRef.current = false;
    liveTokenRef.current = null; // one mint per call: the cache serves this call's reconnects only (verification r2 R2-F3)
    setLiveCaptions([]);
    const client = new LiveClient({
      socketUrl: socketUrlFor(liveConfig.socketPath),
      // The socket token is minted once per call and reused on a reconnect
      // while it has LIVE_TOKEN_SLACK_S of life left; `fresh` (a 4401) mints anew.
      getToken: async ({ fresh } = {}) => {
        const now = Math.floor(Date.now() / 1000);
        const cached = liveTokenRef.current;
        if (!fresh && cached && cached.expiresAt - now > LIVE_TOKEN_SLACK_S) return cached.token;
        const r = await ccmApi.getLiveToken();
        liveTokenRef.current = { token: r.token, expiresAt: Number(r.expiresAt) || 0 };
        return r.token;
      },
      onFrame: onLiveFrame,
      onState: onLiveState,
      onError: onLiveError,
    });
    liveRef.current = client;
    await client.start({ pinnedPatient: pinnedPatient ? { directoryId: pinnedPatient.directoryId } : null });
  }

  function endTalk() {
    if (liveRef.current) liveRef.current.end();
  }

  // The pin chip is the call's patient context too: every change goes down as a
  // `pin` frame (null clears it). The client remembers a pin made while
  // connecting or reconnecting and sends it on `started` / `resumed`.
  useEffect(() => {
    if (liveRef.current && (liveRef.current.state === 'connecting' || liveRef.current.state === 'live')) {
      liveRef.current.pin(pinnedPatient ? pinnedPatient.directoryId : null);
    }
  }, [pinnedPatient ? pinnedPatient.directoryId : null, live.state]);

  // The rail follows the newest caption.
  useEffect(() => {
    const el = liveRailRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [liveCaptions]);

  // Leaving the page (route change or tab close) ends the call on the server at
  // once — billing stops — and releases the mic.
  useEffect(() => {
    // pagehide tells the page too (notify): a page restored from the back/forward
    // cache must not show a live pill for a call that ended when it was left.
    const bye = () => { if (liveRef.current) liveRef.current.destroy({ notify: true }); };
    window.addEventListener('pagehide', bye);
    return () => {
      window.removeEventListener('pagehide', bye);
      if (liveRef.current) liveRef.current.destroy();
      for (const t of liveTimersRef.current) clearTimeout(t);
      liveTimersRef.current.clear();
    };
  }, []);


  // Seed the starter-phrase editor once whoami arrives (their saved list, or the defaults).
  useEffect(() => {
    if (whoami && starterDraft === null) {
      const s = (whoami.starterPhrases && whoami.starterPhrases.length) ? whoami.starterPhrases : STARTER_PHRASES;
      setStarterDraft(s.join('\n'));
    }
  }, [whoami, starterDraft]);

  // Auto-scroll to the newest message only when a turn is ADDED (or send
  // in-flight toggles) — NOT when an existing turn is edited in place (e.g.
  // picking a per-test dx swaps that turn's resolution). Keying on turns.length
  // (not the turns identity) stops the card jumping the page to the bottom on
  // every in-card edit.
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [turns.length, busy]);

  // Auto-grow the composer as the provider types a long order, capped at the
  // textarea's maxHeight (then it scrolls). Reset height to 'auto' first so it
  // also SHRINKS when the text is cleared (after send) or a shorter starter
  // phrase is prefilled. Keyed on `input` so programmatic setInput() resizes too.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [input]);

  // Auto-clear the pin when the typed name diverges from the pinned chart.
  useEffect(() => {
    if (pinnedPatient && pinDiverged(input, `${pinnedPatient.firstName} ${pinnedPatient.lastName}`)) {
      setPinnedPatient(null);
    }
  }, [input, pinnedPatient]);

  // Debounced type-ahead on the fragment after the last "for". Named rather
  // than an inline arrow so a unit witness can execute THIS body: the repo has
  // no jsdom, and a paraphrase of an effect proves nothing about the effect.
  // patientSearchFragment owns every reason not to open the picker — a fragment
  // too short to search, a pinned name the typed text already covers, and (O85)
  // text that already names two patients, where there is no single patient a pin
  // could stand for.
  function runPatientTypeahead() {
    // Retire whatever the PREVIOUS keystroke started: cancel a debounce that has
    // not fired, and bump the sequence so a request already in flight is dropped
    // when it lands — both callbacks below compare against it. Returned from
    // BOTH exits, the suppressed one included. Without it a search issued while
    // the text still named ONE patient came back after the text named two,
    // reopened the picker with the first patient's row, and a click there
    // rewrote the second clause to the first patient's name.
    const invalidate = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      searchSeqRef.current += 1;
    };
    const pinnedName = pinnedPatient ? `${pinnedPatient.firstName} ${pinnedPatient.lastName}` : '';
    const frag = patientSearchFragment(input, pinnedName);
    if (frag == null) {
      setSuggestions([]); setSuggestOpen(false);
      return invalidate;
    }
    invalidate();
    debounceRef.current = setTimeout(async () => {
      const seq = ++searchSeqRef.current;
      try {
        const data = await ccmApi.searchOrderChatPatients(frag);
        if (seq !== searchSeqRef.current) return; // drop stale response
        const results = (data && data.results) || [];
        setSuggestions(results);
        // O83: a fresh result set selects NOTHING. Row 0 used to be armed here,
        // which is what made Enter pin instead of send.
        setActiveIndex(-1);
        setSuggestOpen(results.length > 0);
      } catch {
        if (seq === searchSeqRef.current) { setSuggestions([]); setSuggestOpen(false); }
      }
    }, 200);
    return invalidate;
  }
  useEffect(runPatientTypeahead, [input, pinnedPatient]);

  const speech = useSpeechInput((text) => {
    setInput(prev => prev ? `${prev} ${text}` : text);
    inputRef.current?.focus();
  });

  function selectPatient(p) {
    setInput(prev => applyCanonicalName(prev, `${p.firstName} ${p.lastName}`));
    setPinnedPatient({ directoryId: p.directoryId, firstName: p.firstName, lastName: p.lastName, dob: p.dob });
    setSuggestions([]); setSuggestOpen(false);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) { el.focus(); const len = el.value.length; el.setSelectionRange(len, len); }
    });
  }

  // O83 / O86. Enter SENDS unless the provider arrowed into the picker
  // themselves; composerKeyAction holds that rule and this dispatches it. Named
  // (not inline in the JSX) so a witness can run the real body. The lost
  // keystroke is ratified: typing "…for john" and pressing Enter used to pin
  // John and now sends the message — arrow-then-Enter and clicking the row both
  // still pin, and neither of those is a keystroke meant for something else.
  function onComposerKeyDown(e) {
    const action = composerKeyAction({
      key: e.key, shiftKey: e.shiftKey, suggestOpen, count: suggestions.length, activeIndex,
    });
    if (action === 'default') return;
    e.preventDefault();
    if (action === 'move-down') setActiveIndex(i => (i + 1 >= suggestions.length ? 0 : i + 1));
    else if (action === 'move-up') setActiveIndex(i => (i - 1 < 0 ? suggestions.length - 1 : i - 1));
    else if (action === 'pin') selectPatient(suggestions[activeIndex]);
    else if (action === 'close') { setSuggestOpen(false); setActiveIndex(-1); }
    else if (action === 'send') send();
  }

  // Everything a reply about this thread does to the page, in ONE place: every
  // door the provider can now answer a question through — a typed message, a
  // tapped option (`/pick`), a Confirm, a Cancel, an Undo — comes back in the
  // same shape, so they must land on the transcript the same way. Before the
  // card actions existed only `/send` produced turns, and the per-card send
  // authored sentences of its own instead (R-RX-10's second author).
  function ingestReply(data) {
  const now = Date.now();
  // The seam (PR-CF-P2) can empty `replies[]` outright — a declared resume, or a
  // turn whose ONLY line was the question the card now renders. The turn is what
  // carries the card, the view and the orders, so it is still built: with no
  // text, and Bubble draws no empty bubble for it.
  const lines = Array.isArray(data.replies) ? data.replies : [];
  const carries = !!(data.view || data.resolution
    || (data.extractedOrders || []).length || (data.orderIds || []).length || isBatchReply(data));
  const botTurns = (lines.length ? lines : (carries ? [''] : [])).map((text, i, arr) => {
    const isLast = i === arr.length - 1;
    // Attach order cards to the last bot turn at TWO points:
    //  - the REVIEW step (awaiting_confirmation) when the server staged an
    //    editable resolution (auto-dx flag on) — so the provider can review /
    //    edit per-test dx BEFORE confirming (the LabOrderCard renders from
    //    resolution.cards); orderIds are empty here (nothing created yet).
    //  - COMPLETION for the sent-order summary (Elation # badges).
    // Flag-off sends no resolution, so the review step stays byte-identical
    // (no card) — the editable card only appears when there's a resolution.
    const isCompleted = isLast && data.state === 'completed';
    // R-RX-14: the server attaches a resolution at `awaiting_selection` only
    // for a med-only picker queue, so a resolution at that state IS a card.
    const isReview = isLast
      && (data.state === 'awaiting_confirmation' || data.state === 'awaiting_selection')
      && !!data.resolution;
    const showOrders = isCompleted || isReview;
    // A turn is a BATCH iff the reply carries a batchId and a `resolutions`
    // ARRAY — length irrelevant, aggregate `state` irrelevant (R-S5-45).
    // The aggregate is the lowest-ranked LIVE sibling's state, so gating on
    // `awaiting_confirmation` hid the whole batch the moment any sibling
    // had a question open — the resolved patient's confirmable card too.
    const isBatch = isLast && isBatchReply(data);
    return {
      from: 'bot', text, time: now,
      orders: showOrders ? (data.extractedOrders || []) : null,
      orderIds: isCompleted ? (data.orderIds || []) : null,
      resolution: isReview ? (data.resolution || null) : null,
      // The session this confirmation belongs to — the per-test dx editor
      // (Task 10) threads it into ccmApi.orderEdit so the edit targets the
      // right session. Carried on the review turn (where editing happens),
      // and on every batch turn: EVERY card handler binds only when this is
      // truthy, so a batch that renders cards without it renders them with
      // no edit / preview / Save draft / Send at all. Entry 0's session,
      // else the first sibling's.
      sessionId: isBatch
        ? ((data.resolutions[0] && data.resolutions[0].sessionId)
          || (Array.isArray(data.sessions) && data.sessions[0] && data.sessions[0].sessionId)
          || null)
        : (showOrders ? (data.sessionId || null) : null),
      // A multi-patient dictation (O64): one entry per patient, each with
      // its OWN sessionId, orders and resolution, plus one record per
      // SIBLING — the roster is what renders the patients who have no card
      // yet and what the reconciliation below keys on. The scalar fields
      // above are entry 0 verbatim, so a turn that ignores these renders
      // exactly the first patient's card — never one under the wrong name.
      ...(isBatch
        ? { batchId: data.batchId, batch: data.resolutions, sessions: data.sessions || [] }
        : {}),
      // R-CF-7 / D3: the server's own account of what this row offers — the open
      // question with its keyed options, the Confirm/Cancel/Undo this state
      // allows, and the version a confirm must name. The card controls render
      // from THIS and decide nothing themselves (R-S5-52): the page used to
      // derive "is anything unresolved?" for itself and offer a control the
      // server then refused.
      ...(isLast && data.view ? { view: data.view } : {}),
      // …and under a batch, one view per SIBLING, so each card's Confirm names
      // its own row's version rather than the thread's.
      ...(isBatch && Array.isArray(data.views) ? { views: data.views } : {}),
    };
  });
  // Every reply about a batched thread states what became of every sibling,
  // so every turn showing that batch is brought up to date — not only the
  // one a /batch-confirm was clicked on (R-S5-47). Before this, /send
  // appended and never looked back: after "no for Sarah" her card kept a
  // live Save draft on the turn it was drawn on, one click from a 409, and
  // after "cancel all" the whole batch stayed on screen.
  //
  // The turn built from THIS reply goes through the same reconciliation, so
  // it starts from the same durable evidence rather than as a blank slate:
  // an order the batch had already filed was coming back on the new turn
  // with a live Save draft, directly under the earlier turn's "✓ drafted in
  // Elation #…" for that same order (codex). A non-batch reply is handed
  // back the very array it was given, so the append is what it always was.
  // …and the turn this reply draws inherits the batch's review record, so a
  // card already read does not have to be read again just because the
  // assistant answered (R-S5-51 amended: the record is per BATCH per page
  // load). A thread with no batch gets back the very same turn objects.
  setTurns(t => carryOpenedForward(reconcileBatchTurns([...t, ...botTurns], data)));
  // Server-built chips for THIS question (numbered picks / yes-no) — they
  // replace the fixed regex-gated row while present. Installed only when the
  // reply names the session this page's NEXT message will reach, by the same
  // rule the whole-batch confirm's reply goes through: a row built for
  // another conversation is left alone, and the pin's own chips stand. The
  // pin is judged as it is NOW (the ref), not as it was when this send left
  // — a History click during the round trip must not re-open the hole.
  // R-CF-7: when the reply carries a VIEW, the composer row is the view's own
  // question — options that name themselves. `quickReplies` is the text
  // protocol's chip row (a digit, a bare "yes") and is left to the undeclared
  // clients it is still served to; installing it here would put a positional
  // control back on a page that has stopped sending positions. The branch below
  // stays for a reply with no view — there is none after this slice, but a stale
  // tab mid-deploy must not crash.
  recordViews(data);
  // The server's chip row still rides every reply and is still installed through
  // the one OWNER gate (`chipRowIsOurs` — a row built for another conversation is
  // left alone, R-S5-52 amended / PR-S5b-6). What changed is that this client no
  // longer RENDERS it: those chips post a DIGIT, and the server refuses a digit
  // from a declared request, so drawing one would be a control the server refuses
  // (departure 4). The row the provider taps is `view.question.options`.
  if (chipRowIsOurs(data, resumedSessionIdRef.current)) {
    setDynamicReplies(Array.isArray(data.quickReplies) ? data.quickReplies.filter(r => r && r.label && r.send) : []);
  }
  if (data.view) setView(data.view);
  // Order finished — drop the pins so the next order starts fresh.
  if (data.state === 'completed' || data.state === 'cancelled') {
    setPinnedPatient(null);
    pinSession(null);
  }
  }

  // ── Answering by NAME (R-CF-7) ──────────────────────────────────────────────
  //
  // A tap sends the OPTION — which conversation, which question, which choice —
  // and the version of the card it was read off. A question that changed under
  // the provider (another tab, the phone, a memo opened in Order Chat) refuses
  // the tap and refreshes the card, instead of answering a different question or
  // a different conversation with it.
  //
  // The user-side transcript entry is the LABEL the provider tapped: their own
  // choice, in their own words. It is not a claim about a write (R-RX-10) — the
  // sentence about what WAS written is the server's.
  //
  // PR-CF-4: ONE door for every open question, whether its control is a chip
  // under the transcript (the page's current view) or a button on the card
  // (`onView` — the live view of THAT card's conversation, so a card on an
  // earlier turn can never answer another conversation's question).
  async function pickOption(option, onView) {
    const v = onView || view;
    const q = v && v.question;
    if (!q || busy) return;
    setDynamicReplies([]);
    setTurns((t) => [...t, { from: 'me', text: option ? option.label : `Skip "${q.label || 'this'}"`, time: Date.now() }]);
    setBusy(true);
    try {
      const data = await ccmApi.orderChatPick({
        sessionId: v.sessionId,
        questionId: q.questionId,
        ...(option ? { optionId: option.optionId } : { skip: true }),
        expect: { version: v.version },
      });
      ingestReply(data);
    } catch (e) {
      refusedTurn(e, "Couldn't answer that");
    } finally {
      setBusy(false);
    }
  }

  // A refusal is an ANSWER: the server says what happened and hands back the
  // refreshed card, so the page shows both rather than a dead error bubble.
  function refusedTurn(e, lead) {
    const body = (e && e.body) || {};
    setTurns((ts) => [...ts, { from: 'bot', text: (e && e.message) || `${lead} — something went wrong.`, time: Date.now(), error: true }]);
    // A refusal carries the REFRESHED card — that is the whole point of refusing
    // with a view — so it moves the live view on exactly as a success does.
    recordViews(body);
    if (body.view) setView(body.view);
  }

  // Cancel, naming its session. The chat "no" it replaces is refused from this
  // client now, and a card another confirm has claimed is refused here too —
  // with the line that says so, and nothing written.
  async function cancelSession(sessionId, version) {
    if (busy || !sessionId) return;
    setTurns((t) => [...t, { from: 'me', text: 'Cancel', time: Date.now() }]);
    setBusy(true);
    try {
      ingestReply(await ccmApi.orderChatCancel(sessionId, version ? { version } : undefined));
    } catch (e) {
      refusedTurn(e, "Couldn't cancel that");
    } finally {
      setBusy(false);
    }
  }

  // Undo THIS conversation's drafts (fork c). The text "undo" retracted "the
  // most recent completed session" on a key the web, the phone and every memo
  // hand-off share — which is the ambiguity R-CF-7 closes.
  async function undoSession(sessionId) {
    if (busy || !sessionId) return;
    setTurns((t) => [...t, { from: 'me', text: 'Undo', time: Date.now() }]);
    setBusy(true);
    try {
      ingestReply(await ccmApi.orderChatUndo(sessionId));
    } catch (e) {
      refusedTurn(e, "Couldn't undo that");
    } finally {
      setBusy(false);
    }
  }

  async function send(textOverride, pinOverride) {
    const message = String(textOverride ?? input).trim();
    if (!message || busy) return;
    setInput('');
    setDynamicReplies([]); // the question just got answered — stale chips must not linger
    const userTurn = { from: 'me', text: message, time: Date.now() };
    setTurns(t => [...t, userTurn]);
    setBusy(true);
    // Retries pass the pin that was active when the ORIGINAL send failed —
    // resending "CBC for John" under a since-changed pin would order for the
    // wrong patient (the server treats the pin as authoritative).
    const pin = pinOverride !== undefined ? pinOverride : (pinnedPatient ? { directoryId: pinnedPatient.directoryId } : null);
    // O78 / R-S5-51: while a batch is on screen, the turn carries the cards this
    // provider has actually opened, so a TYPED "confirm all" meets the same
    // review gate the button does and hears the same sentence. It is a review
    // HINT and never an authorisation input — confirm-all grants nothing a
    // card-by-card confirm does not — so nothing depends on it being true, and
    // a turn with no batch on screen sends no such field at all.
    const liveBatchTurn = [...turns].reverse().find(isBatchTurn) || null;
    try {
      const data = await ccmApi.sendOrderChat(message, pin, resumedSessionId,
        liveBatchTurn ? openedSessionIdsFor(liveBatchTurn) : null);
      ingestReply(data);
    } catch (e) {
      // A failed SEND is retryable — carry the original message AND pin on the
      // error turn so the bubble can offer "Try again" instead of a dead end.
      setTurns(t => [...t, { from: 'bot', text: `Couldn't send that — ${e.message || 'something went wrong'}.`, time: Date.now(), error: true, retryText: message, retryPin: pin }]);
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  }

  // Per-test dx editor (Task 10): apply a structured edit to the active session's
  // orders and re-render STRICTLY from the returned resolution (single source of
  // truth). The card hands us { action, payload:{ testIndex?, ... } }; we thread
  // the session + the order's index. orderIndex is the order's position within
  // the turn — index-aligned with both extractedOrders and resolution.cards
  // (toResolution does `orders.map(orderToCard)`), so the card's render index IS
  // the orderIndex. We rewrite that same turn's resolution in place.
  // Consume-and-resend for a failed send(): the retry payload is REMOVED from
  // the error turn before resending, so "Try again" can only fire once — a
  // second click after success would duplicate a non-idempotent order.
  function retryTurn(turnIndex) {
    const t = turns[turnIndex];
    if (!t || !t.retryText) return;
    const { retryText, retryPin } = t;
    setTurns(ts => ts.map((x, j) => (j === turnIndex ? { ...x, retryText: null, retryPin: null } : x)));
    send(retryText, retryPin ?? null);
  }

  // Resolves to { ok:true } | { ok:false, error, status, stale } so a card can
  // render a refusal WHERE THE PROVIDER IS LOOKING. It never throws, and callers
  // that ignore the value are unaffected.
  //
  // `stale` is the ONE decision the card reads: this function owns the network,
  // so it is the only thing that can know whether the card is able to recover.
  // A refused swap (400) means the card is out of date — the remedy is a FRESH
  // list, so the session's card is re-read and installed, and the row shows the
  // refusal in place with no chat turn (O20). Everything else — 404, 409, 500, a
  // network drop, or a re-read that cannot deliver a resolution — has no
  // card-local remedy, so it keeps today's chat bubble; that last case is
  // settled by derivation from O20's premise ("the remedy is a card gesture")
  // rather than by a ruling, and is open to veto.
  // Which session a card belongs to. A batch turn holds N entries, each an
  // ordinary single-patient card set; entryIndex null is the single-patient
  // turn, unchanged. The refreshed resolution goes back through the lib's
  // `withResolution`, keyed by that session — never by the index, which the
  // reconciliation can shift while the edit is in flight.
  // The three gestures that count as reviewing a card — the chevron, the
  // requisition preview, a committed edit — recorded on EVERY turn of that
  // card's batch, because the review is of the card and not of the copy a later
  // reply re-drew. It is also what keeps the button and `openedSessionIds[]` one
  // rule (O78). Nothing is written anywhere but the turns: a reload re-arms the
  // gate, deliberately (R-S5-51). A single-patient turn has no `batchId`, so
  // this is inert with the flag off.
  function _recordOpened(ts, turnIndex, sessionId) {
    const t = ts[turnIndex];
    if (!t || t.batchId == null || sessionId == null) return ts;
    return markOpenedInBatch(ts, t.batchId, sessionId);
  }
  function markCardOpened(turnIndex, sessionId) {
    if (sessionId == null) return;
    setTurns((ts) => _recordOpened(ts, turnIndex, sessionId));
  }
  function toggleCard(turnIndex, sessionId) {
    setTurns((ts) => {
      const next = ts.map((t, i) => (i === turnIndex ? toggleExpanded(t, sessionId) : t));
      // Opening it is the review; closing it again does not take that back.
      return isExpanded(next[turnIndex], sessionId) ? _recordOpened(next, turnIndex, sessionId) : next;
    });
  }

  // The VERSION of the card this control was rendered from (R-CF-7 / D2). A
  // confirm names it, and a card a second reader changed underneath is refused
  // with the refreshed card instead of filing what the provider did not read.
  // Per-sibling first (a batch renders N cards from N rows, each with its own
  // version), then the turn's own, then the page's current view.
  function versionFor(turn, sessionId) {
    const want = sessionId == null ? null : String(sessionId);
    if (!want) return null;
    const v = viewOfTurn(viewsRef.current, turn, sessionId);
    if (v && v.version) return v.version;
    if (view && String(view.sessionId) === want && view.version) return view.version;
    return null;
  }
  // What the SERVER says this card may do right now (R-S5-52 / D4). `null` when
  // the turn carries no view — a reply from before this slice, or a card whose
  // row the page has not heard about since; the control then renders as it
  // always did and the server's own gate is the backstop, as it has always been.
  function cardSession(turn, entryIndex) {
    if (entryIndex == null) return turn && turn.sessionId;
    const entry = turn && Array.isArray(turn.batch) ? turn.batch[entryIndex] : null;
    return entry && entry.sessionId;
  }
  function cardOrders(turn, entryIndex) {
    if (entryIndex == null) return (turn && turn.orders) || [];
    const entry = turn && Array.isArray(turn.batch) ? turn.batch[entryIndex] : null;
    return (entry && entry.extractedOrders) || [];
  }
  // `inPlace` (O180): an edit made on a refill sheet's card — its refusal is told on the
  // line (`inPlace.error`), never as a transcript bubble, and the reply's view and chips
  // stay off the composer (`inPlace.view`): a sheet card never owns the chat's chip row.
  async function applyOrderEdit(turnIndex, entryIndex, orderIndex, edit, inPlace = null) {
    const turn = turns[turnIndex];
    const sessionId = cardSession(turn, entryIndex);
    // no live session to edit (shouldn't happen for an order turn)
    if (!sessionId) return { ok: false, error: 'No active session to edit.' };
    try {
      const data = await ccmApi.orderEdit({ sessionId, orderIndex, ...edit });
      // A 200 carrying no resolution is an edit the route persisted but could
      // not render. Reported as success it made the card announce a swap the row
      // does not show, so it is a failure: the provider is told instead of
      // misled. (The route no longer produces this — it builds before it
      // persists — but the card must not depend on that to stay honest.)
      if (!data || !data.resolution) throw new Error('The card could not refresh.');
      // An edit that landed is a card the provider has read (O64's gate) — and
      // it is a reply ABOUT this batch, so it reconciles the turns already on
      // screen the way every other reply about a batch does (R-S5-47, L71): the
      // edited sibling's roster state becomes the state the route just returned,
      // on THIS turn and on any other turn still showing the same batch. Without
      // it "Confirm all reviewed cards" stays at "0 of 2" after both siblings
      // are picked, refusing a confirm the server would accept.
      const batchId = turn.batchId;
      setTurns(ts => _recordOpened(
        ts.map((t, i) => {
          if (i === turnIndex) return withResolution(t, sessionId, data.resolution, data.state);
          if (batchId != null && t && String(t.batchId) === String(batchId)) {
            return withRosterState(t, sessionId, data.state);
          }
          return t;
        }),
        turnIndex, sessionId,
      ));
      // The controls this thread offers changed with the row (R-S5-46/52): a
      // pick answers the picker, so the digit chips go and the review pair
      // arrives. The route builds that row for this edit; installed through the
      // same owner gate `/send` uses, so a reply built for another conversation
      // is left alone.
      if (!inPlace && chipRowIsOurs(data, resumedSessionIdRef.current)) {
        setDynamicReplies(Array.isArray(data.quickReplies) ? data.quickReplies.filter(r => r && r.label && r.send) : []);
      }
      // …and so did the VERSION and the controls: an edit is a write, and the
      // card the provider is looking at must confirm against what the edit left,
      // not against the render it was drawn from (R-CF-7 / D2). Without this the
      // very next Confirm answered 409 `view_stale`, and so did the one after it.
      recordViews(data);
      if (data.view) (inPlace ? inPlace.view : setView)(data.view);
      if (inPlace && inPlace.ok) inPlace.ok();
      return { ok: true };
    } catch (e) {
      const msg = (e && e.body && e.body.error) || (e && e.message) || 'Could not apply that edit.';
      const status = e && e.status;
      let stale = false;
      if (edit && edit.action === 'pick-test-alternative' && status === 400) {
        const fresh = await ccmApi.orderChatSessionCard(sessionId).catch(() => null);
        if (fresh && fresh.resolution) {
          setTurns(ts => ts.map((t, i) => (i === turnIndex ? withResolution(t, sessionId, fresh.resolution) : t)));
          stale = true;
        }
      }
      if (!stale && inPlace) inPlace.error(refusalText(e));
      else if (!stale) setTurns(ts => [...ts, { from: 'bot', text: msg, error: true }]);
      return { ok: false, error: msg, status, stale };
    }
  }

  // B2 — "Review & send" for the direct-fax card types: quest home-draws and
  // generic (non-connected) labs tagged genericLab. previewQuestOrder fetches the
  // requisition PDF; submitQuestOrder fires the draft/fax and appends a result
  // turn. Both dispatch on the staged order's type/tag and target the active
  // session by sessionId (same gate as applyOrderEdit).
  function _isGenericLab(turn, entryIndex, orderIndex) {
    const o = cardOrders(turn, entryIndex)[orderIndex];
    return !!(o && o.type === 'lab' && o.genericLab);
  }

  async function previewQuestOrder(turnIndex, entryIndex, orderIndex) {
    const turn = turns[turnIndex];
    const sessionId = cardSession(turn, entryIndex);
    if (!sessionId) return { errors: ['No active session to preview.'] };
    // Opening the requisition IS reading the card (O64's gate) — whatever the
    // render then does with it.
    markCardOpened(turnIndex, sessionId);
    try {
      return _isGenericLab(turn, entryIndex, orderIndex)
        ? await ccmApi.genericPreview(sessionId, orderIndex)
        : await ccmApi.questPreview(sessionId, orderIndex);
    } catch (e) {
      const errs = (e && e.body && e.body.errors) || (e && e.message && [e.message]) || ['Preview failed.'];
      return { errors: errs };
    }
  }

  async function submitQuestOrder(turnIndex, entryIndex, orderIndex, arg) {
    const turn = turns[turnIndex];
    const sessionId = cardSession(turn, entryIndex);
    if (!sessionId) return;
    const mode = arg && arg.mode;
    try {
      if (_isGenericLab(turn, entryIndex, orderIndex)) {
        const o = cardOrders(turn, entryIndex)[orderIndex];
        const labName = (o.genericLab && o.genericLab.name) || 'the lab';
        const data = await ccmApi.genericSend(sessionId, orderIndex, mode, arg && arg.fax);
        const msg = mode === 'fax'
          ? `Faxed the ${labName} requisition to ${data.recipient || labName} (Elation order #${data.labOrderId}).`
          : `Drafted the ${labName} requisition in Elation (order #${data.labOrderId}) — sign & send the letter there to fax it.`;
        setTurns((ts) => [...ts, { from: 'bot', text: msg }]);
        for (const w of (data.warnings || [])) {
          setTurns((ts) => [...ts, { from: 'bot', text: w, error: true }]);
        }
        return;
      }
      const data = await ccmApi.questSend(sessionId, orderIndex, mode);
      const msg = mode === 'fax'
        ? `Faxed the Quest requisition to ${data.recipient || 'Quest'} (Elation report #${data.reportId}).`
        : `Saved a Quest draft in Elation (report #${data.reportId}) — review & sign it there to send.`;
      setTurns((ts) => [...ts, { from: 'bot', text: msg }]);
    } catch (e) {
      const errs = (e && e.body && e.body.errors && e.body.errors.join(' ')) || (e && e.message) || 'Order failed.';
      setTurns((ts) => [...ts, { from: 'bot', text: errs, error: true }]);
    }
  }

  // Per-card "Save draft" / "Send now": dispatch ONE staged order via /order-send
  // and flip that card read-only. mode 'draft' (default) leaves an unsigned Elation
  // draft (byte-identical to before); mode 'send' signs + transmits the recipient-
  // routed letter to its destination (referrals + facility-routed diagnostics). The
  // session goes partially_sent until every card is sent (or "Send all"/"yes"
  // finishes the rest). On completion, drop the pin.
  async function submitOrderSend(turnIndex, entryIndex, orderIndex, mode = 'draft') {
    const turn = turns[turnIndex];
    const sessionId = cardSession(turn, entryIndex);
    if (!sessionId) return;
    try {
      const version = versionFor(turn, sessionId);
      const data = await ccmApi.orderSend(sessionId, orderIndex, mode === 'send' ? 'send' : 'draft',
        version ? { version } : undefined);
      setTurns((ts) => ts.map((t, i) => {
        if (i !== turnIndex) return t;
        // Persist `sent` alongside the created order so the durable card badge can
        // tell a transmitted order ("✓ faxed/emailed…") from a draft ("✓ drafted in
        // Elation #id"). The server returns created + sent as siblings; data.created
        // alone has no `.sent`, which would mislabel a real Send now as "drafted".
        return { ...t, sentOrders: { ...(t.sentOrders || {}), [cardSlot(entryIndex == null ? null : sessionId, orderIndex)]: { ...data.created, sent: data.sent } } };
      }));
      if (data.sendFailed) {
        // The referral was created but the send couldn't be confirmed (sign/send PATCH
        // failed). data.sent is already false, so the durable card badge falls back to
        // "✓ drafted in Elation" — never a false "✓ faxed". Warn honestly here too.
        setTurns((ts) => [...ts, { from: 'bot', text: "Created the referral, but I couldn't confirm it was sent — open it in Elation to review & send.", error: true }]);
      }
      // R-RX-10 / O120: ONE transcript, authored by the server. The three
      // sentences this branch used to write — "Signed and transmitted in
      // Elation.", "Faxed/emailed to …", "All orders drafted in Elation." /
      // "All orders sent." — were a SECOND author making claims about a write it
      // had not watched: the client could only see a DICTATED fax, so a letter
      // that went to a contact's own on-file number read as a bare "signed and
      // transmitted", and "All orders drafted" was said off a boolean. The
      // sentence now comes back in `replies[]` from the same
      // `buildCompletionMessage` the chat "yes" speaks, so a card's Confirm and
      // a "yes" over the same row leave the identical line — and it rides the
      // same ingestion every other reply does, which is what installs the
      // controls the new state offers (closing the lingering-chips nit).
      ingestReply(data);
      if (data.complete) setPinnedPatient(null);
    } catch (e) {
      const errs = (e && e.body && e.body.errors && e.body.errors.join(' ')) || (e && e.message) || 'Order send failed.';
      setTurns((ts) => [...ts, { from: 'bot', text: errs, error: true }]);
      // A refused confirm hands back the refreshed card; the next click must use
      // it rather than the version that was just refused.
      recordViews((e && e.body) || null);
      if (e && e.body && e.body.view) setView(e.body.view);
    }
  }

  // "Send all" over a multi-patient batch (O64). N ordinary per-card confirms,
  // walked in roster order by the server; it never transmits, so a referral is
  // still a draft and a home draw still needs its own Review & send. The reply
  // reports per patient, and the entries that are still open come back so the
  // cards re-render in place.
  async function confirmWholeBatch(turnIndex, batchId) {
    try {
      const data = await ccmApi.batchConfirm(batchId, resumedSessionId);
      const lines = batchResultLines(turns[turnIndex], data);
      // Identity, not position: markBatchResults keys every completion by
      // (sessionId, orderIndex) and marks only the orders the primitive named.
      // The confirm is a reply about the batch like any other, so the turns
      // BESIDE this one are reconciled by the same rule (R-S5-47) — a sibling
      // this confirm finished, or found already cancelled, must not keep live
      // controls on an earlier turn either.
      // …and what filed collapses behind its badge while what still needs the
      // provider stays open: a card they must act on is never hidden behind a
      // chevron. The expansion follows the outcome the results were folded into,
      // so "what happened to this card" is decided once.
      setTurns((ts) => ts.map((t, i) => (i === turnIndex ? collapseFinished(markBatchResults(t, data)) : reconcileBatchTurn(t, data))));
      // The row is whatever the server now offers — installed exactly as a
      // /send reply's is. Clearing it outright looked right for the chips this
      // click consumed and was wrong for a sibling still being ASKED: her
      // numbered choices are untouched by a batch confirm, and they vanished
      // (codex, real PG). The server knows which of the two it is; the client
      // does not have to guess. Per-card sends leave the row alone — they
      // address one card and its other chips are still live.
      //
      // …but only when the reply's chips answer the session THIS page will
      // send to. The reply names its owner, so a row built for another session
      // — the page pinned to one open question, the server's active row being
      // a different one — is refused and the pin's own chips are left standing.
      // Without that, a positional digit chip labelled with one patient posted
      // into another patient's session (codex, real PG): a pending card under
      // the wrong chart, one confirm from a wrong-chart order.
      const heldPin = resumedSessionIdRef.current == null ? null : String(resumedSessionIdRef.current);
      // …and so does the VIEW the roster came back with — one per sibling plus the
      // owner row's — so the cards this confirm left behind render and confirm
      // against what it left, exactly as every other door's reply does
      // (R-CF-7 / D3).
      recordViews(data);
      if (chipRowIsOurs(data, heldPin)) {
        setDynamicReplies(Array.isArray(data.quickReplies) ? data.quickReplies.filter((r) => r && r.label && r.send) : []);
      }
      if (lines.length) setTurns((ts) => [...ts, { from: 'bot', text: lines.join('\n'), time: Date.now() }]);
      // The confirmed batch finishing ends the DICTATION subject — that pin is
      // about who the provider was ordering for, and letting it go is the safe
      // direction. It says nothing about where the thread is pinned: a page
      // resumed onto another conversation's open question kept answering for
      // that question (its chips are installed just above), and dropping its
      // pin here sent the very next tap unpinned, into whatever row sorted
      // active — a chip labelled with one patient staging an order on
      // another's chart (codex, real PG). The session pin goes only when this
      // reply's own roster says the pinned row is over.
      if (data.state === 'completed') setPinnedPatient(null);
      if (pinnedRowTerminal(data, heldPin)) pinSession(null);
    } catch (e) {
      const msg = (e && e.body && e.body.error) || (e && e.message) || 'Could not send those cards.';
      setTurns((ts) => [...ts, { from: 'bot', text: msg, error: true, time: Date.now() }]);
    }
  }

  function loadHistory() {
    setHistoryOpen(o => !o);
    if (!history) {
      ccmApi.listOrderChatSessions().then(setHistory).catch(() => setHistory([]));
    }
  }

  // Resume one of this user's conversations (history click, or the voice-memo
  // → Order Chat hand-off deep link): fetch the session's card and ingest it
  // through the same turn shape /send responses use. NON-TERMINAL threads come
  // back LIVE — subsequent sends continue that session (server pins by the
  // user's phone key, and the transfer refreshed the session TTL).
  async function resumeSession(sessionId) {
    try {
      const data = await ccmApi.orderChatSessionCard(sessionId);
      const now = Date.now();
      // The server's own line when it sent one; nothing when it sent `replies: []`
      // and a VIEW instead (the state label renders from `view.state`). The
      // fallback below is for a reply from before this slice — a tab open across
      // the deploy — which has neither.
      const replies = Array.isArray(data.replies) && data.replies.length
        ? data.replies
        : (data.view ? [] : ['Resuming this conversation.']);
      const live = rowIsLive(data.state);
      // Pin subsequent sends to THIS conversation while it is live — EVERY
      // non-terminal state, the same set the server calls live. This gate used
      // to read `state.startsWith('awaiting')`, so a half-sent or in-flight card
      // opened from History was installed and then sent under NO handle: the tap
      // went to whatever thread sorted newest and filed that thread's unreviewed
      // card (R-S5-51 amended; `rowIsLive` states the set once).
      pinSession(live ? (data.sessionId || sessionId) : null);
      // The thread's opening bubble is this surface's own; everything else about
      // the reply — its turns, its chips, and the VIEW it carries — goes through
      // the one ingestion every other door uses, as this function's own comment
      // has always claimed. It did not, and a resumed card therefore had no
      // version: the first Confirm on it answered 400 `expect.version is
      // required`, and the Voice-Log hand-off and every History click were dead
      // ends (R-CF-7 / D3).
      if (data.message) setTurns((t) => [...t, { from: 'user', text: data.message, time: now }]);
      ingestReply({ ...data, replies, sessionId: data.sessionId || sessionId });
      setHistoryOpen(false);
    } catch (e) {
      setTurns((t) => [...t, { from: 'bot', text: `Couldn't open that conversation — ${e.message || 'something went wrong'}.`, time: Date.now(), error: true }]);
    }
  }

  // One-shot resume signal (deep link / Voice Log hand-off).
  const consumedResumeRef = useRef(0);
  useEffect(() => {
    if (!resumeRequest || !resumeRequest.ts || resumeRequest.ts === consumedResumeRef.current) return;
    consumedResumeRef.current = resumeRequest.ts;
    onResumeConsumed?.();
    if (resumeRequest.sessionId) resumeSession(resumeRequest.sessionId);
  }, [resumeRequest]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Group-home refill sheets (PR-E) ─────────────────────────────────────────
  //
  // A sheet is ONE turn in the transcript (`kind: 'refillSheet'`), updated in place as
  // its job moves: queued → reading → matching and drafting → done. Its needs-attention
  // cards arrive in /send's own batch shape and live on that turn's batch fields
  // (lib/refill-sheet withSheetJob → lib/order-batch markBatchResults), so every reply
  // about one of them reconciles the sheet like any other batch turn (R-S5-47), and the
  // cards are drawn by this page's own Bubble (`sheetCard`). A reply's own turn about the
  // sheet's batch keeps only its words on screen (foldSheetBatchTurns, at render), or
  // every answer would draw the sheet's cards again under it.
  function applySheetJob(rosterId, job) {
    setTurns((ts) => ts.map((t) => (t && t.kind === 'refillSheet' && t.rosterId === rosterId
      ? { ...withSheetJob(t, job), pollStopped: null } : t)));
    if (job && job.batch) recordViews(job.batch);
  }
  function markSheetStopped(rosterId, why) {
    setTurns((ts) => ts.map((t) => (t && t.kind === 'refillSheet' && t.rosterId === rosterId
      ? { ...t, pollStopped: why === 'final' || why === 'failed' || why === 'discarded' ? null : why } : t)));
  }
  function watchSheet(rosterId) {
    const running = sheetPollersRef.current.get(rosterId);
    if (running && running.isRunning()) return;
    const poller = createSheetPoller({
      fetchJob: () => ccmApi.getRosterSheet(rosterId),
      onJob: (job) => applySheetJob(rosterId, job),
      onStop: (why) => markSheetStopped(rosterId, why),
    });
    sheetPollersRef.current.set(rosterId, poller);
    poller.start();
  }
  // One read on demand (after a card moved, a search pick, a discard). A sheet that is
  // not final yet is polled again instead.
  async function refreshSheet(rosterId) {
    const t = turns.find((x) => x && x.kind === 'refillSheet' && x.rosterId === rosterId);
    const final = t && t.sheet && t.sheet.status === 'done' && t.sheet.staging
      && (t.sheet.staging.status === 'staged' || t.sheet.staging.status === 'failed');
    if (!final) { watchSheet(rosterId); return; }
    try {
      applySheetJob(rosterId, await ccmApi.getRosterSheet(rosterId));
    } catch (e) {
      markSheetStopped(rosterId, e && e.status === 404 ? 'gone' : 'errors');
    }
  }
  function openSheet(rosterId, extra = {}) {
    if (!rosterId) return;
    setSheetDialogOpen(false);
    const existing = turns.some((t) => t && t.kind === 'refillSheet' && t.rosterId === rosterId);
    if (existing) {
      const el = typeof document !== 'undefined' ? document.querySelector(`[data-roster-id="${rosterId}"]`) : null;
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      refreshSheet(rosterId);
      return;
    }
    setTurns((ts) => [...ts, ...(extra.uploadedFrom ? [{ from: 'me', text: `Uploaded a refill sheet — ${extra.uploadedFrom}`, time: Date.now() }] : []),
      newSheetTurn(rosterId, extra.facilityName ? { sheet: { rosterId, status: 'queued', facilityName: extra.facilityName, lines: [] } } : {})]);
    watchSheet(rosterId);
  }
  useEffect(() => () => {
    for (const p of sheetPollersRef.current.values()) p.stop();
    sheetPollersRef.current.clear();
  }, []);
  // One card of a sheet, drawn by the page's own Bubble from the sheet turn's batch
  // entry for that session, with the page's own handlers bound to the SHEET turn and
  // that entry's real index in it.
  //
  // O180: every control on a sheet card answers IN PLACE — the pick, the Confirm, the
  // Cancel and an edit's refusal go through the card's own keyed door for ITS session
  // (below), never through the chat: no transcript entry, no scroll, no focus jump. The
  // line re-renders from the server's answer.
  function sheetCard(turnIndex, turn, sessionId) {
    const ct = cardTurnFor(turn, sessionId);
    if (!ct) return null;
    const e = ct.entryIndex;
    return (
      <Bubble
        compact
        turn={ct.turn}
        views={viewsBySession}
        onEditOrder={e != null ? (_x, orderIndex, edit) => applyOrderEdit(turnIndex, e, orderIndex, edit, sheetEditSink(turn.rosterId, sessionId)) : undefined}
        onSubmitOrder={e != null ? (_x, orderIndex, arg) => submitQuestOrder(turnIndex, e, orderIndex, arg) : undefined}
        onPreviewOrder={e != null ? (_x, orderIndex) => previewQuestOrder(turnIndex, e, orderIndex) : undefined}
        onSendOrder={e != null ? (_x, orderIndex, mode) => sendSheetCard(turn, sessionId, orderIndex, mode) : undefined}
        onCancelCard={(sid) => cancelSheetCard(turn, sid || sessionId)}
        onToggleCard={(sid) => toggleCard(turnIndex, sid)}
        onPickOption={(cardView, option) => pickSheetOption(turn.rosterId, cardView, option)}
      />
    );
  }

  // ── A sheet card answers IN PLACE (O180) ────────────────────────────────────
  //
  // The same keyed doors the chat's cards use (R-CF-7): /pick names the card's session,
  // its question, the option and the version it was read off; /order-send and /cancel
  // name the session and the version. What differs is where the answer lands. A sheet
  // card's reply reconciles the sheet's batch and installs the card's views — and does
  // NOTHING else the chat's ingestion does: no turn appended (the transcript grows, and
  // the page's auto-scroll jumps to the bottom), no page-wide `busy` (the same scroll,
  // the typing dots, a disabled composer), no chip row and no composer view (a sheet card
  // never owns the dictation's chips), no pin dropped (sheet cards are card-addressed,
  // D-3). The server's sentence is spoken in the sheet's live region, the sheet re-reads
  // itself (a pick need not move a card's state, so the sheet's own state watch would
  // miss it), and focus goes back to the line (RefillSheetTurn's focus effect).
  // Keyed `${rosterId}:${session or '*'}`: one answer in flight per card PER SHEET, so a
  // second sheet's Confirm all is never a silent dead tap (D4).
  const sheetPendingRef = useRef(new Set());
  // I-FOCUS (D2): the page-wide input counter the focus rule reads.
  useEffect(() => { if (typeof document !== 'undefined') installInputCounter(document); }, []);
  function isSheetTurn(t, rosterId) {
    return !!(t && t.kind === 'refillSheet' && t.rosterId === rosterId);
  }
  function sheetOwnView(v) {
    // The composer's view moves only when it is ALREADY about this card's session.
    if (v && v.sessionId != null) setView((cur) => (cur && String(cur.sessionId) === String(v.sessionId) ? v : cur));
  }
  function ingestSheetReply(data) {
    setTurns((ts) => reconcileBatchTurns(ts, data));
    recordViews(data);
    if (data) sheetOwnView(data.view);
  }
  async function answerSheetCard(rosterId, key, run, { onReply, said } = {}) {
    // The provider's input so far — taken FIRST, when the answer starts (I-FOCUS).
    const inputAt = inputCount();
    const k = String(key);
    const slot = `${rosterId}:${k}`;
    if (sheetPendingRef.current.has(slot)) return { ok: false, skipped: true };
    sheetPendingRef.current.add(slot);
    const sheet = turns.find((t) => isSheetTurn(t, rosterId));
    const lineNo = k === '*' ? null : lineOfSession(sheet, k);
    try {
      return await answerInPlace({
        lineNo,
        run,
        onReply: (data) => { if (onReply) onReply(data); ingestSheetReply(data); },
        onRefused: (body) => { recordViews(body); if (body) sheetOwnView(body.view); },
        mark: (patch) => setTurns((ts) => withInPlace(ts, rosterId, k, patch)),
        refresh: () => refreshSheet(rosterId),
        said,
        inputAt,
      });
    } finally {
      sheetPendingRef.current.delete(slot);
    }
  }
  function pickSheetOption(rosterId, cardView, option) {
    const q = cardView && cardView.question;
    if (!q || cardView.sessionId == null) return undefined;
    return answerSheetCard(rosterId, cardView.sessionId, () => ccmApi.orderChatPick({
      sessionId: cardView.sessionId,
      questionId: q.questionId,
      ...(option ? { optionId: option.optionId } : { skip: true }),
      expect: { version: cardView.version },
    }));
  }
  function sendSheetCard(turn, sessionId, orderIndex, mode = 'draft') {
    const version = versionFor(turn, sessionId);
    return answerSheetCard(turn.rosterId, sessionId,
      () => ccmApi.orderSend(sessionId, orderIndex, mode === 'send' ? 'send' : 'draft', version ? { version } : undefined),
      {
        // The card's durable badge, as the chat's per-card confirm writes it.
        onReply: (data) => setTurns((ts) => ts.map((t) => (isSheetTurn(t, turn.rosterId)
          ? { ...t, sentOrders: { ...(t.sentOrders || {}), [cardSlot(sessionId, orderIndex)]: { ...data.created, sent: data.sent } } }
          : t))),
      });
  }
  function cancelSheetCard(turn, sessionId) {
    if (!sessionId) return undefined;
    const version = versionFor(turn, sessionId);
    return answerSheetCard(turn.rosterId, sessionId, () => ccmApi.orderChatCancel(sessionId, version ? { version } : undefined));
  }
  // The sheet's "Confirm all reviewed cards": the same batch confirm, its per-patient
  // report spoken (and shown) on the sheet rather than appended to the chat.
  function confirmSheetAll(turn) {
    if (!turn || turn.batchId == null) return undefined;
    const before = turn;
    return answerSheetCard(turn.rosterId, '*', () => ccmApi.batchConfirm(turn.batchId, resumedSessionId), {
      onReply: (data) => setTurns((ts) => ts.map((t) => (isSheetTurn(t, turn.rosterId)
        ? collapseFinished(markBatchResults(t, data)) : reconcileBatchTurn(t, data)))),
      said: (data) => batchResultLines(before, data).join(' '),
    });
  }
  // An edit on a sheet card: refused → said on the line (its role="alert" speaks it); a
  // later successful edit clears it (D1 c); its view stays off the composer.
  function sheetEditSink(rosterId, sessionId) {
    return {
      error: (msg) => setTurns((ts) => withInPlace(ts, rosterId, sessionId, { error: msg })),
      ok: () => setTurns((ts) => withInPlace(ts, rosterId, sessionId, { error: null })),
      view: sheetOwnView,
    };
  }
  const shownTurns = foldSheetBatchTurns(turns);
  const refillSheetsEnabled = !!(whoami && whoami.refillSheetsEnabled);

  const autoDx = !!(whoami && whoami.autoDxFromChart);

  async function toggleAutoDx() {
    if (savingPref) return;
    const next = !autoDx;
    setWhoami(w => ({ ...(w || {}), autoDxFromChart: next }));
    setSavingPref(true);
    try {
      await ccmApi.updateOrderChatSettings({ autoDxFromChart: next });
    } catch {
      setWhoami(w => ({ ...(w || {}), autoDxFromChart: !next })); // revert on failure
    } finally {
      setSavingPref(false);
    }
  }

  // Direct send (O178): anything but an explicit 'direct' — including a
  // whoami still loading — reads as Card, so the switch never shows Direct on
  // that the server would not apply.
  const directSend = !!(whoami && whoami.orderSendMode === 'direct');

  async function toggleDirectSend() {
    if (savingPref) return;
    const next = directSend ? 'card' : 'direct';
    setWhoami(w => ({ ...(w || {}), orderSendMode: next }));
    setSavingPref(true);
    try {
      await ccmApi.updateOrderChatSettings({ orderSendMode: next });
    } catch {
      setWhoami(w => ({ ...(w || {}), orderSendMode: next === 'direct' ? 'card' : 'direct' })); // revert on failure
    } finally {
      setSavingPref(false);
    }
  }

  const defaultFasting = (whoami && whoami.defaultFasting) || 'fasting_random';

  async function changeDefaultFasting(next) {
    if (savingPref || next === defaultFasting) return;
    const prev = defaultFasting;
    setWhoami(w => ({ ...(w || {}), defaultFasting: next }));
    setSavingPref(true);
    try {
      await ccmApi.updateOrderChatSettings({ defaultFasting: next });
    } catch {
      setWhoami(w => ({ ...(w || {}), defaultFasting: prev })); // revert on failure
    } finally {
      setSavingPref(false);
    }
  }

  const defaultReferralAuth = (whoami && whoami.defaultReferralAuth) || 'consult';

  async function changeDefaultReferralAuth(next) {
    if (savingPref || next === defaultReferralAuth) return;
    const prev = defaultReferralAuth;
    setWhoami(w => ({ ...(w || {}), defaultReferralAuth: next }));
    setSavingPref(true);
    try {
      await ccmApi.updateOrderChatSettings({ defaultReferralAuth: next });
    } catch {
      setWhoami(w => ({ ...(w || {}), defaultReferralAuth: prev })); // revert on failure
    } finally {
      setSavingPref(false);
    }
  }

  const starters = (whoami && whoami.starterPhrases && whoami.starterPhrases.length)
    ? whoami.starterPhrases
    : STARTER_PHRASES;

  async function saveStarters() {
    if (starterDraft === null || savingStarters) return;
    const phrases = starterDraft.split('\n').map(s => s.trim()).filter(Boolean).slice(0, 8);
    const current = (whoami && whoami.starterPhrases && whoami.starterPhrases.length) ? whoami.starterPhrases : STARTER_PHRASES;
    if (JSON.stringify(phrases) === JSON.stringify(current)) return; // unchanged — skip the save
    const prev = whoami && whoami.starterPhrases;
    setWhoami(w => ({ ...(w || {}), starterPhrases: phrases }));
    setSavingStarters(true);
    try {
      await ccmApi.updateOrderChatSettings({ starterPhrases: phrases });
    } catch {
      setWhoami(w => ({ ...(w || {}), starterPhrases: prev })); // revert on failure
      setStarterDraft(((prev && prev.length) ? prev : STARTER_PHRASES).join('\n'));
    } finally {
      setSavingStarters(false);
    }
  }

  const cannotOrder = whoami && whoami.canPlaceOrders === false;

  return (
    <div className="orderchat-page" style={{
      display: 'flex', flexDirection: 'column',
      height: 'calc(100vh - 96px)', maxWidth: 760, margin: '0 auto', padding: '0 16px',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 0', borderBottom: '1px solid #f3f4f6',
      }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#111' }}>Order Chat</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            {whoami?.displayName ? `${whoami.displayName} · ` : ''}
            labs, refills & referrals in plain English
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="orderchat-chip" onClick={loadHistory} aria-expanded={historyOpen}>
            {historyOpen ? 'Close history' : 'History'}
          </button>
          <button className="orderchat-chip" onClick={() => setSettingsOpen(o => !o)} aria-expanded={settingsOpen}>
            {settingsOpen ? 'Close settings' : 'Settings'}
          </button>
        </div>
      </div>

      {cannotOrder && (
        <div style={{
          margin: '12px 0', padding: '10px 14px', background: '#fef3c7',
          border: '1px solid #fde68a', borderRadius: 8, fontSize: 13, color: '#92400e',
        }}>
          You're not yet mapped to an Elation physician. An admin needs to set
          this up before orders can be placed. (Settings → SMS Bot → Providers.)
        </div>
      )}

      {historyOpen && (
        <div style={{
          margin: '10px 0', padding: '8px', background: '#fafafa',
          border: '1px solid #e5e7eb', borderRadius: 10, maxHeight: 220, overflowY: 'auto',
        }}>
          {history == null && (
            <div role="status" aria-live="polite" aria-label="Loading history" className="skeleton">
              <div className="sk-line w70" />
              <div className="sk-line w40" />
              <div className="sk-line w70" />
            </div>
          )}
          {history && history.length === 0 && (
            <div style={{ fontSize: 13, color: '#888' }}>No conversations yet.</div>
          )}
          {history && history.map(s => (
            <button key={s.id} type="button" onClick={() => resumeSession(s.id)} style={{
              padding: '6px 10px', fontSize: 13, color: '#444', cursor: 'pointer',
              borderBottom: '1px solid #f3f4f6', display: 'flex', gap: 8, alignItems: 'baseline',
              width: '100%', textAlign: 'left', background: 'transparent',
              border: 'none', borderRadius: 0,
            }} title="Open this conversation">
              <span style={{ fontSize: 11, color: '#888', minWidth: 90 }}>{new Date(s.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.message}</span>
              <span style={{ fontSize: 11, color: s.state === 'completed' ? '#0f766e' : s.state === 'cancelled' ? '#888' : '#b45309' }}>{s.state}</span>
            </button>
          ))}
        </div>
      )}

      {settingsOpen && (
        <div style={{
          margin: '10px 0', padding: '12px 14px', background: '#fafafa',
          border: '1px solid #e5e7eb', borderRadius: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <button
              type="button"
              role="switch"
              aria-checked={autoDx}
              aria-label="Auto-attach diagnosis from chart"
              onClick={toggleAutoDx}
              disabled={savingPref}
              style={{
                flexShrink: 0, width: 42, height: 24, borderRadius: 999, border: 'none',
                background: autoDx ? '#0f766e' : '#d1d5db', position: 'relative',
                cursor: savingPref ? 'wait' : 'pointer', transition: 'background .15s',
                padding: 0, marginTop: 2,
              }}
            >
              <span style={{
                position: 'absolute', top: 2, left: autoDx ? 20 : 2, width: 20, height: 20,
                borderRadius: '50%', background: '#fff', transition: 'left .15s',
                boxShadow: '0 1px 2px rgba(0,0,0,.2)',
              }} />
            </button>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#111' }}>
                Auto-attach diagnosis from chart
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                When you order labs without saying a diagnosis, the bot suggests one per test —
                a matching active problem from the patient's chart, or a screening code — for you
                to review and sign in Elation. Order sets keep their own codes.
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginTop: 14, paddingTop: 14, borderTop: '1px solid #eef2f7' }}>
            <button
              type="button"
              role="switch"
              aria-checked={directSend}
              aria-label="Create drafts directly"
              onClick={toggleDirectSend}
              disabled={savingPref}
              style={{
                flexShrink: 0, width: 42, height: 24, borderRadius: 999, border: 'none',
                background: directSend ? '#0f766e' : '#d1d5db', position: 'relative',
                cursor: savingPref ? 'wait' : 'pointer', transition: 'background .15s',
                padding: 0, marginTop: 2,
              }}
            >
              <span style={{
                position: 'absolute', top: 2, left: directSend ? 20 : 2, width: 20, height: 20,
                borderRadius: '50%', background: '#fff', transition: 'left .15s',
                boxShadow: '0 1px 2px rgba(0,0,0,.2)',
              }} />
            </button>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#111' }}>
                Create drafts directly
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                Medication, lab and imaging orders with nothing left to decide go straight to Elation as
                unsigned drafts — no Confirm tap. Anything with a question, a warning, a controlled
                substance, or a patient you didn't name still shows a card. Undo stays on the card for an hour.
              </div>
            </div>
          </div>
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #eef2f7' }}>
            <label htmlFor="orderchat-fasting" style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#111' }}>
              Default lab Patient Condition
            </label>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2, marginBottom: 8 }}>
              Pre-fills the fasting field on lab drafts when you don't say otherwise. Order sets keep
              their own, and you can still change it at sign in Elation.
            </div>
            <select
              id="orderchat-fasting"
              value={defaultFasting}
              disabled={savingPref}
              onChange={(e) => changeDefaultFasting(e.target.value)}
              style={{
                width: '100%', maxWidth: 360, padding: '8px 10px', borderRadius: 8,
                border: '1px solid #d1d5db', fontSize: 14, fontFamily: 'inherit',
                background: '#fff', cursor: savingPref ? 'wait' : 'pointer',
              }}
            >
              {FASTING_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #eef2f7' }}>
            <label htmlFor="orderchat-referral-auth" style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#111' }}>
              Default referral authorization
            </label>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2, marginBottom: 8 }}>
              Used on referral drafts when you don't say otherwise. Say “for ongoing treatment”
              or “procedure only” in a message to override it; you can still change it at sign in Elation.
            </div>
            <select
              id="orderchat-referral-auth"
              value={defaultReferralAuth}
              disabled={savingPref}
              onChange={(e) => changeDefaultReferralAuth(e.target.value)}
              style={{
                width: '100%', maxWidth: 360, padding: '8px 10px', borderRadius: 8,
                border: '1px solid #d1d5db', fontSize: 14, fontFamily: 'inherit',
                background: '#fff', cursor: savingPref ? 'wait' : 'pointer',
              }}
            >
              {REFERRAL_AUTH_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #eef2f7' }}>
            <label htmlFor="orderchat-starters" style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#111' }}>
              Starter phrases
            </label>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2, marginBottom: 8 }}>
              The quick-fill chips on a new chat — one per line. Tapping a chip prefills the box (it
              doesn't send). End a phrase with “for ” to drop the cursor right before the patient name.
            </div>
            <textarea
              id="orderchat-starters"
              value={starterDraft ?? ''}
              disabled={savingStarters}
              onChange={(e) => setStarterDraft(e.target.value)}
              onBlur={saveStarters}
              rows={5}
              placeholder={'CBC and BMP for \nrefill aspirin for '}
              style={{
                width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db',
                fontSize: 14, fontFamily: 'inherit', resize: 'vertical', minHeight: 96,
                background: savingStarters ? '#f9fafb' : '#fff', outline: 'none',
              }}
            />
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
              Saved when you click away. Up to 8 phrases. Clear all to restore the defaults.
            </div>
          </div>
        </div>
      )}

      <div ref={scrollerRef} style={{
        flex: 1, overflowY: 'auto', padding: '14px 0',
        display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        {turns.length === 0 && (
          <div style={{
            margin: 'auto', textAlign: 'center', maxWidth: 480, padding: 24, color: '#4b5563',
          }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>💬</div>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6, color: '#111' }}>
              Draft orders in plain English
            </div>
            {/* What a confirm does, since O59 (2026-09-03): it FILES the order.
                A home draw is saved unsigned and reaches Quest only from the
                card's Review & send, under the rendered PDF — the old line
                ("sends immediately") promised a fax the "yes" no longer sends. */}
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
              Drafts land in Elation for your signature. Confirm on the order card
              files it — review the card first. A Quest home draw is saved as a
              draft; it is faxed to Quest only from the card's Review &amp; send,
              under the rendered PDF. Home draws named at another lab become an
              ordinary lab order to that lab, with the collection details in the
              requisition's lab instructions.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'stretch' }}>
              {starters.map(p => (
                <button key={p} className="orderchat-chip" style={{ textAlign: 'left' }}
                        onClick={() => {
                          setInput(p);
                          requestAnimationFrame(() => {
                            const el = inputRef.current;
                            if (el) { el.focus(); el.setSelectionRange(p.length, p.length); }
                          });
                        }}>
                  {p}{/\s$/.test(p) && <span style={{ color: '#9ca3af' }}>patient name</span>}
                </button>
              ))}
            </div>
          </div>
        )}
        {shownTurns.map((turn, i) => (turn && turn.folded && !turn.text && !turn.error ? null : turn && turn.kind === 'refillSheet' ? (
          <RefillSheetTurn
            key={`sheet-${turn.rosterId}`}
            turn={turn}
            views={viewsBySession}
            renderCard={(sessionId) => sheetCard(i, turn, sessionId)}
            // O180: the sheet's own option buttons answer their card in place.
            onPickOption={(cardView, option) => pickSheetOption(turn.rosterId, cardView, option)}
            onConfirmAll={turn.batchId ? () => confirmSheetAll(turn) : undefined}
            onRefresh={() => refreshSheet(turn.rosterId)}
            onUploadAgain={refillSheetsEnabled ? () => setSheetDialogOpen(true) : undefined}
            directSendOn={directSend}
          />
        ) : (
          <Bubble
            key={i}
            turn={turn}
            views={viewsBySession}
            onEditOrder={turn.sessionId ? (entryIndex, orderIndex, edit) => applyOrderEdit(i, entryIndex, orderIndex, edit) : undefined}
            onSubmitOrder={turn.sessionId ? (entryIndex, orderIndex, arg) => submitQuestOrder(i, entryIndex, orderIndex, arg) : undefined}
            onPreviewOrder={turn.sessionId ? (entryIndex, orderIndex) => previewQuestOrder(i, entryIndex, orderIndex) : undefined}
            onSendOrder={turn.sessionId ? (entryIndex, orderIndex, mode) => submitOrderSend(i, entryIndex, orderIndex, mode) : undefined}
            // A3: "Send all" over a BATCH is the batch-confirm route — a bare
            // "yes" over N cards on N charts confirms nothing (R-S5-1).
            // On a single-patient turn it is the WHOLE-SESSION confirm
            // (`orderIndex: null`) — the same primitive, the same gate and the
            // same server sentence the chat "yes" ran, now naming the session and
            // the view it was clicked on instead of being a word typed into a
            // composer the server no longer parses (R-CF-7, O120).
            onSendAll={turn.batchId ? () => confirmWholeBatch(i, turn.batchId) : (turn.sessionId ? () => submitOrderSend(i, null, null, 'draft') : undefined)}
            // Bound to the VIEW's session, not the turn's: `/order-send`'s
            // receipt carries no top-level `sessionId` (its keys are byte-
            // identical for the phone), so the completion turn that offers Undo
            // had nothing to bind to and the control never rendered.
            onCancelCard={turn.view && turn.view.sessionId ? (sid) => cancelSession(sid || turn.view.sessionId, versionFor(turn, sid || turn.view.sessionId)) : undefined}
            onUndoCard={turn.view && turn.view.sessionId ? (sid) => undoSession(sid || turn.view.sessionId) : undefined}
            onToggleCard={(sessionId) => toggleCard(i, sessionId)}
            onRetry={turn.error && turn.retryText && !busy ? () => retryTurn(i) : undefined}
            // PR-CF-4: an open slot on a card answers through the same pick door
            // the chips use, naming the card's own view.
            onPickOption={(cardView, option) => pickOption(option, cardView)}
          />
        )))}
        {busy && (
          <div style={{ alignSelf: 'flex-start', padding: '6px 10px', color: '#9ca3af' }}>
            <span className="orderchat-dot" /><span className="orderchat-dot" /><span className="orderchat-dot" />
          </div>
        )}
      </div>

      {/* The OPEN question's options, from the server's own view (R-CF-7). Each
          chip posts the OPTION — the conversation, the question and the choice —
          and the version of the card it was read off, so a question another
          reader answered first refuses the tap and refreshes the card instead of
          answering a different question with it. Before this the chip posted the
          DIGIT the reply parser expects, which is a position resolved against
          whatever heads the queue at receipt.

          The fixed ['yes','no','undo','help'] row is gone: those words were the
          SMS adapter's protocol, and Confirm / Cancel / Undo live on the card. */}
      {view && view.question && view.question.prompt && !busy && (
        <div data-role="question-prompt" style={{ padding: '4px 2px 0', fontSize: 13, color: '#374151' }}>
          {view.question.prompt}
        </div>
      )}
      {view && view.question && !busy && (
        <div style={{
          display: 'flex', gap: 8, padding: '4px 0', flexWrap: 'wrap',
        }}>
          {view.question.options.map((o, i) => (
            <button
              key={`${o.optionId}-${i}`}
              className="orderchat-chip"
              title={o.label}
              onClick={() => pickOption(o)}
              // O24: a lab option's chip reads "Vitamin D, 25-Hydroxy (081950,
              // CPT 82306, added 2017)" — about 52 characters, and ellipsizing it
              // at 340px would cut off the very facts that tell two same-name
              // generations apart. Wraps to a second line instead; `title` stays
              // as the full-text fallback.
              style={{ maxWidth: 480, whiteSpace: 'normal', textAlign: 'left' }}
            >
              {o.label}
            </button>
          ))}
          {/* SKIP last, so it never sits where an option chip was a moment ago.
              Only a LAB line has one — the server says which (S-10). */}
          {view.question.skippable && (
            <button className="orderchat-chip" onClick={() => pickOption(null)}>
              {`Skip "${view.question.label || 'this'}"`}
            </button>
          )}
        </div>
      )}
      {/* A reply with no view at all is a response from before this slice, seen
          only by a tab open across the deploy. `dynamicReplies` still holds that
          reply's TEXT chips (a digit, a bare "yes"), and the state is kept so
          nothing here crashes — but they are not rendered: every request this
          bundle sends declares the web adapter, and the server refuses a digit
          from a declared request outright. A chip that posts one would be a
          control the server refuses, which is the rule this slice is enforcing
          (R-S5-52). The composer still works; a reload brings the view back. */}

      <div className="orderchat-composer">
        <ul
          className="orderchat-suggest"
          role="listbox"
          id="orderchat-suggest-list"
          aria-label="Patient suggestions"
          hidden={!suggestOpen || suggestions.length === 0}
        >
          {suggestions.map((p, i) => (
            <li
              key={p.directoryId}
              id={`orderchat-suggest-${i}`}
              role="option"
              aria-selected={activeIndex >= 0 && i === activeIndex}
              className={'orderchat-suggest-item' + (activeIndex >= 0 && i === activeIndex ? ' is-active' : '')}
              onMouseDown={(e) => { e.preventDefault(); selectPatient(p); }}
            >
              <span className="orderchat-suggest-name">{p.firstName} {p.lastName}</span>
              {p.dob ? <span className="orderchat-suggest-dob">DOB {formatDob(p.dob)}</span> : null}
            </li>
          ))}
        </ul>
        {pinnedPatient && (
          <div className="orderchat-pin">
            <span aria-hidden="true">📌</span>
            <strong>{pinnedPatient.firstName} {pinnedPatient.lastName}</strong>
            {pinnedPatient.dob ? <span className="orderchat-pin-dob">· DOB {formatDob(pinnedPatient.dob)}</span> : null}
            <button type="button" className="orderchat-pin-x" aria-label="Clear pinned patient" onClick={() => setPinnedPatient(null)}>✕</button>
          </div>
        )}
        {liveConfig && live.state !== 'idle' && (
          <div className="orderchat-live">
            {/* Only the pill is a live region: the rail re-renders on every delta and must not re-announce itself. */}
            <span role="status" aria-live="polite" className={`orderchat-live-pill orderchat-live-pill--${live.state === 'live' ? (live.phase || 'listening') : live.state}`}>
              {LIVE_PILL_TEXT(live)}
            </span>
            {liveCaptions.length > 0 && (
              <div className="orderchat-live-rail" role="log" aria-label="Live captions" ref={liveRailRef}>
                {liveCaptions.map((c, i) => (
                  <div key={`${c.time}-${i}`} className={`orderchat-live-cap orderchat-live-cap--${c.speaker}`}>
                    <span className="orderchat-live-cap-who">{c.speaker === 'assistant' ? 'Assistant' : 'You'}</span>
                    <span className="orderchat-live-cap-text">{c.text}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          style={{
            display: 'flex', gap: 8, padding: '12px 0', borderTop: '1px solid #f3f4f6',
            alignItems: 'flex-end',
          }}
        >
        <ComposerPlusMenu
          refillSheetsEnabled={refillSheetsEnabled}
          disabled={cannotOrder}
          onRefillSheet={() => setSheetDialogOpen(true)}
        />
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onComposerKeyDown}
          placeholder={cannotOrder ? "View-only — admin must map you to Elation first" : "Say your order naturally…"}
          rows={1}
          disabled={cannotOrder}
          style={{
            flex: 1, padding: '10px 14px', borderRadius: 18, border: '1px solid #d1d5db',
            fontSize: 14, fontFamily: 'inherit', resize: 'none', maxHeight: 120,
            overflowY: 'auto', outline: 'none',
          }}
          aria-label="Order message"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={suggestOpen}
          aria-controls="orderchat-suggest-list"
          aria-activedescendant={suggestOpen && suggestions.length && activeIndex >= 0 ? `orderchat-suggest-${activeIndex}` : undefined}
        />
        {speech.supported && (
          <button
            type="button"
            onClick={() => speech.listening ? speech.stop() : speech.start()}
            disabled={cannotOrder || live.state === 'connecting' || live.state === 'live'}
            className={speech.listening ? 'orderchat-mic-pulse' : ''}
            style={{
              width: 40, height: 40, borderRadius: 999,
              background: speech.listening ? '#dc2626' : '#f3f4f6',
              color: speech.listening ? '#ffffff' : '#374151',
              border: 'none', cursor: cannotOrder ? 'not-allowed' : 'pointer',
              fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            aria-label={speech.listening ? 'Stop dictation' : 'Dictate'}
            title={speech.listening ? 'Stop dictation' : 'Dictate'}
          >
            🎙
          </button>
        )}
        {liveConfig && (() => {
          // ONE element whose label and action flip, so keyboard focus survives the switch.
          const on = live.state === 'connecting' || live.state === 'live';
          return (
            <button
              type="button"
              className={on ? 'orderchat-talk is-live' : 'orderchat-talk'}
              onClick={on ? endTalk : () => { startTalk(); }}
              disabled={!on && cannotOrder}
              aria-pressed={on}
              aria-label={on ? 'End live voice' : 'Talk to the assistant'}
              title={on ? 'End live voice' : 'Talk to the assistant (live voice)'}
            >
              {on ? 'End' : 'Talk'}
            </button>
          );
        })()}
        <button
          type="submit"
          disabled={busy || !input.trim() || cannotOrder}
          style={{
            padding: '10px 18px', borderRadius: 18, border: 'none',
            background: !input.trim() || cannotOrder ? '#9ca3af' : '#0f766e',
            color: '#ffffff', fontSize: 14, fontWeight: 600,
            cursor: !input.trim() || cannotOrder ? 'not-allowed' : 'pointer',
          }}
        >
          Send
        </button>
      </form>
      </div>
      {sheetDialogOpen && refillSheetsEnabled && (
        <RefillSheetDialog
          onClose={() => setSheetDialogOpen(false)}
          onUploaded={(rosterId, facility) => openSheet(rosterId, {
            facilityName: facility ? facility.name : null,
            uploadedFrom: facility ? facility.name : 'group home',
          })}
          onOpenSheet={(rosterId) => openSheet(rosterId)}
        />
      )}
    </div>
  );
}
